import { useState, useRef } from 'react';
import polygonClipping from 'polygon-clipping';
import { lockScroll, unlockScroll } from '../utils/scrollLock';
import { getMaxDepth as getMaxDepthUtil } from '../utils/svgDimensions';
import { getConnectionKey as getConnectionKeyUtil, getConnectionPointKey as getConnectionPointKeyUtil } from '../utils/svgHelpers';
import { getSoilGroup, SOIL_GROUP_COLORS } from '../utils/soilGroups';
import { layerHasOwnColor } from '../utils/layerColors';

export function useConnections({ boreholes, getSvgCoordinates, scale, horizontalScale, snapToOrtho = false, snapToGrid = false, gridSize = 1.0, snapToEdge = false, snapToBorehole = true, initialData, terrainProfile = null }) {
  const initSettings = initialData?.settings || {};
  const initConnections = initialData?.connections || {};
  const [connectLayers, setConnectLayers] = useState(initSettings.connectLayers ?? false);
  const [autoConnectByName, setAutoConnectByName] = useState(initSettings.autoConnectByName ?? false);
  const [autoConnectMode, setAutoConnectMode] = useState(initSettings.autoConnectMode || 'byName'); // 'byName' | 'byGeotechnicalLayer' | 'byWeatheringProfile' | 'byRomanPrefix' | 'bySoilGroup'
  const [manualConnections, setManualConnections] = useState(initConnections.manualConnections || []);
  const [selectedLayerForConnection, setSelectedLayerForConnection] = useState(null);
  const [excludedAutoConnections, setExcludedAutoConnections] = useState(initConnections.excludedAutoConnections || []);
  const [connectionPointAdjustments, setConnectionPointAdjustments] = useState(initConnections.connectionPointAdjustments || {});
  const [draggingConnectionPoint, setDraggingConnectionPoint] = useState(null);
  const [manualConnectionMode, setManualConnectionMode] = useState(false);
  const [interpolationMode, setInterpolationMode] = useState(false);
  const [interpolationPoints, setInterpolationPoints] = useState(initConnections.interpolationPoints || {});
  const [draggingInterpolation, setDraggingInterpolation] = useState(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [freeDrawMode, setFreeDrawMode] = useState(false);
  const [freeDrawClipMode, setFreeDrawClipMode] = useState('clipNew'); // 'clipNew' = starsze mają priorytet, 'clipOld' = nowsza nadpisuje starsze
  const [freeDrawPoints, setFreeDrawPoints] = useState([]); // max 4 snap points
  const [freeDrawPreview, setFreeDrawPreview] = useState(null); // {x, y} hover position
  const [selectedFreeDraw, setSelectedFreeDraw] = useState(null); // id zaznaczonego freeDraw (główne)
  const [selectedFreeDrawIds, setSelectedFreeDrawIds] = useState([]); // wielokrotny wybór (Ctrl+klik) — grupa do wspólnego dragu
  const [editingFreeDraw, setEditingFreeDraw] = useState(null); // id freeDraw w trybie edycji wierzchołków
  const [freeDrawEditVerticesMode, setFreeDrawEditVerticesMode] = useState(false); // tryb edycji wierzchołków (przycisk)
  const [freeDrawSnapVertices, setFreeDrawSnapVertices] = useState(false); // snap do wierzchołków innych polygonów
  const [freeDrawFillGaps, setFreeDrawFillGaps] = useState(false); // wypełniaj luki po zmniejszeniu polygonu
  const [freeDrawExpandMode, setFreeDrawExpandMode] = useState(false); // tryb rozszerzania granic — klik dodaje wcięcie
  const [freeDrawExpandClip, setFreeDrawExpandClip] = useState(true); // nakładanie — przycina sąsiednie warstwy
  const [draggingFreeDrawControl, setDraggingFreeDrawControl] = useState(null); // {connId, edgeIndex}
  const [draggingFreeDrawPoint, setDraggingFreeDrawPoint] = useState(null); // {connId, pointIndex}
  const [draggingFreeDrawEdge, setDraggingFreeDrawEdge] = useState(null); // {connId, edgeIndex, startSvg, p1Start, p2Start}
  const [draggingFreeDrawPolygon, setDraggingFreeDrawPolygon] = useState(null); // {connId, startSvg, ptsStart: [{x,y}]}
  const preDragShapeRef = useRef(null); // kształt polygonu przed dragiem (ring w metrach)
  const [splitMode, setSplitMode] = useState(false); // tryb podziału polygonu linią
  const [splittingConnId, setSplittingConnId] = useState(null); // id polygonu do podzielenia
  const [splitLinePoints, setSplitLinePoints] = useState([]); // [{x,y}, {x,y}]
  const [splitPickMode, setSplitPickMode] = useState(false); // tryb wyboru polygonu do podziału (klik na polygon → wejście w splitMode)
  const [splitLinePreview, setSplitLinePreview] = useState(null); // {x,y} pod kursorem
  const [polylineSplitMode, setPolylineSplitMode] = useState(false); // tryb cięcia polilinią przez wiele freeDraw warstw
  const [polylineSplitPoints, setPolylineSplitPoints] = useState([]); // [{x,y}, ...] w SVG
  const [polylineSplitPreview, setPolylineSplitPreview] = useState(null); // {x,y} pod kursorem
  const [polylineSplitStatus, setPolylineSplitStatus] = useState(null); // {cut, skipped, autoConnectSkipped, ts} — komunikat po cięciu
  const [contextMenuFreeDraw, setContextMenuFreeDraw] = useState(null); // {clientX, clientY, connId}
  const [lensInLayerMode, setLensInLayerMode] = useState(false); // tryb rysowania soczewki wewnątrz polygonu
  const [lensInLayerParentId, setLensInLayerParentId] = useState(null); // id polygonu-rodzica dla przycięcia
  const [cutHoleMode, setCutHoleMode] = useState(false); // tryb rysowania dziury w polygonie
  const [mergePolygonsMode, setMergePolygonsMode] = useState(false); // tryb scalania dwóch polygonów w jeden
  const [mergeFromConnId, setMergeFromConnId] = useState(null); // id pierwszego klikniętego polygonu (drugi go scala)
  const [cutHoleParentId, setCutHoleParentId] = useState(null); // id polygonu z którego wycinamy
  const [copiedFreeDrawProps, setCopiedFreeDrawProps] = useState(null); // {color, pattern, name} — pipeta dla prawy-klik menu
  const [editPropsDialog, setEditPropsDialog] = useState(null); // {connId, clientX, clientY} — szybki dialog edycji właściwości
  const [draggingPolygonLabel, setDraggingPolygonLabel] = useState(null); // {mcId, offsetX, offsetY} — drag etykiety nazwy polygonu
  const [stickyLayerLock, setStickyLayerLock] = useState(false); // tryb "rysuj tylko w wybranej warstwie" — sticky, nie wyłącza się po jednym rysunku

  const handlePolygonLabelDragStart = (e, mcId, currentSvgX, currentSvgY) => {
    e.stopPropagation();
    e.preventDefault();
    lockScroll();
    const { x, y } = getSvgCoordinates(e);
    setDraggingPolygonLabel({
      mcId,
      offsetX: x - currentSvgX,
      offsetY: y - currentSvgY,
    });
  };

  const handlePolygonLabelDragMove = (e) => {
    if (!draggingPolygonLabel) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);
    const newX = x - draggingPolygonLabel.offsetX;
    const newY = y - draggingPolygonLabel.offsetY;
    const pixelsPerM = scale / 10;
    const distanceM = (newX - 100) / horizontalScale;
    const depthM = (newY - 80) / pixelsPerM;
    setManualConnections(prev => prev.map(mc =>
      mc.id === draggingPolygonLabel.mcId
        ? { ...mc, labelPos: { distanceM, depthM } }
        : mc
    ));
  };

  const handlePolygonLabelDragEnd = () => {
    if (!draggingPolygonLabel) return;
    unlockScroll();
    setDraggingPolygonLabel(null);
  };

  const resetPolygonLabelPos = (mcId) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== mcId) return mc;
      const { labelPos, ...rest } = mc;
      return rest;
    }));
  };

  const getConnectionKey = getConnectionKeyUtil;
  const getConnectionPointKey = getConnectionPointKeyUtil;

  const handleInterpolationPointDrag = (e, connectionKey) => {
    if (!interpolationMode) return;
    const { x, y } = getSvgCoordinates(e);
    setDraggingInterpolation({ key: connectionKey, x, y });

    // Zablokuj przewijanie strony, ale zachowaj szerokość scrollbara
    lockScroll();
  };

  const handleInterpolationMouseMove = (e) => {
    if (!draggingInterpolation) return;
    e.preventDefault(); // Zapobiega przewijaniu
    const { x, y } = getSvgCoordinates(e);
    setInterpolationPoints(prev => ({
      ...prev,
      [draggingInterpolation.key]: { x, y }
    }));
  };

  const handleInterpolationMouseUp = () => {
    // Przywróć przewijanie strony i usuń padding
    unlockScroll();

    setDraggingInterpolation(null);
  };

  const resetInterpolationPoint = (connectionKey) => {
    setInterpolationPoints(prev => {
      const newPoints = { ...prev };
      delete newPoints[connectionKey];
      return newPoints;
    });
  };

  const handleConnectionPointMouseDown = (e, pointKey, defaultX, defaultY) => {
    e.stopPropagation();
    e.preventDefault();

    // Zablokuj przewijanie strony
    lockScroll();

    setDraggingConnectionPoint({ key: pointKey, defaultX, defaultY });
  };

  const handleConnectionPointMouseMove = (e) => {
    if (!draggingConnectionPoint) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);

    setConnectionPointAdjustments(prev => ({
      ...prev,
      [draggingConnectionPoint.key]: {
        x: x - draggingConnectionPoint.defaultX,
        y: y - draggingConnectionPoint.defaultY
      }
    }));
  };

  const handleConnectionPointMouseUp = () => {
    unlockScroll();

    setDraggingConnectionPoint(null);
  };

  const resetConnectionPoint = (pointKey) => {
    setConnectionPointAdjustments(prev => {
      const newAdjustments = { ...prev };
      delete newAdjustments[pointKey];
      return newAdjustments;
    });
  };

  const deleteManualConnection = (connectionId) => {
    // Usuń połączenie o danym ID
    setManualConnections(manualConnections.filter(mc => mc.id !== connectionId));

    // Usuń także wszystkie punkty kontrolne związane z tym połączeniem
    const keysToDelete = Object.keys(connectionPointAdjustments).filter(key => key.includes(`-${connectionId}`));
    if (keysToDelete.length > 0) {
      setConnectionPointAdjustments(prev => {
        const newAdj = { ...prev };
        keysToDelete.forEach(key => delete newAdj[key]);
        return newAdj;
      });
    }

    const interpKeysToDelete = Object.keys(interpolationPoints).filter(key => key.includes(`-${connectionId}`));
    if (interpKeysToDelete.length > 0) {
      setInterpolationPoints(prev => {
        const newPoints = { ...prev };
        interpKeysToDelete.forEach(key => delete newPoints[key]);
        return newPoints;
      });
    }
  };

  const deleteConnection = (conn) => {
    // Funkcja do usuwania zarówno automatycznych jak i ręcznych połączeń
    if (conn.isManual) {
      // Dla ręcznych - usuń z manualConnections
      deleteManualConnection(conn.connectionId);
    } else {
      // Dla automatycznych - dodaj do listy wykluczeń
      const excludeKey = `${conn.fromBorehole.id}-${conn.toBorehole.id}-${conn.name}`;

      // Dodaj do listy wykluczeń jeśli jeszcze nie istnieje
      if (!excludedAutoConnections.includes(excludeKey)) {
        setExcludedAutoConnections([...excludedAutoConnections, excludeKey]);
      }

      // Usuń punkty kontrolne dla automatycznego połączenia
      const keysToDelete = Object.keys(connectionPointAdjustments).filter(key => key.includes(`-${conn.connectionId}`));
      if (keysToDelete.length > 0) {
        setConnectionPointAdjustments(prev => {
          const newAdj = { ...prev };
          keysToDelete.forEach(key => delete newAdj[key]);
          return newAdj;
        });
      }

      const interpKeysToDelete = Object.keys(interpolationPoints).filter(key => key.includes(`-${conn.connectionId}`));
      if (interpKeysToDelete.length > 0) {
        setInterpolationPoints(prev => {
          const newPoints = { ...prev };
          interpKeysToDelete.forEach(key => delete newPoints[key]);
          return newPoints;
        });
      }
    }
  };

  /**
   * Snap do najbliższej krawędzi warstwy na najbliższym otworze.
   * Zwraca {boreholeId, boreholeIndex, boundaryIndex, x, y} lub null.
   * boundaryIndex: 0 = góra otworu, 1 = dół 1. warstwy, 2 = dół 2. warstwy, ...
   */
  const snapToLayerBoundary = (svgX, svgY) => {
    if (!snapToBorehole) return null;
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepthUtil(boreholes);

    let closestBorehole = null;
    let closestDist = Infinity;
    let closestIndex = -1;

    boreholes.forEach((bh, idx) => {
      const bhX = 100 + bh.distance * horizontalScale;
      const dist = Math.abs(svgX - bhX);
      if (dist < closestDist && dist < 80) {
        closestDist = dist;
        closestBorehole = bh;
        closestIndex = idx;
      }
    });

    if (!closestBorehole) return null;

    const bhX = 100 + closestBorehole.distance * horizontalScale;
    const depthOffset = (maxDepth - closestBorehole.depth) * pixelsPerMeter;
    const topY = 80 + depthOffset;

    // Zbierz Y wszystkich krawędzi warstw
    const boundaries = [{ y: topY, idx: 0 }];
    let cumulativeThickness = 0;
    closestBorehole.layers.forEach((layer, i) => {
      cumulativeThickness += layer.thickness;
      boundaries.push({ y: topY + cumulativeThickness * pixelsPerMeter, idx: i + 1 });
    });

    // Snap do krawędzi tylko gdy bardzo blisko (próg pionowy ~8px)
    const EDGE_SNAP_THRESHOLD = 8;
    let closestEdge = null;
    let minDist = Infinity;
    boundaries.forEach(b => {
      const d = Math.abs(svgY - b.y);
      if (d < minDist) {
        minDist = d;
        closestEdge = b;
      }
    });

    if (closestEdge && minDist <= EDGE_SNAP_THRESHOLD) {
      return {
        boreholeId: closestBorehole.id,
        boreholeIndex: closestIndex,
        boundaryIndex: closestEdge.idx,
        x: bhX,
        y: closestEdge.y,
        edge: true
      };
    }

    // Poza progiem krawędzi: snap X do otworu, Y wolne (zapisane jako depthM)
    // Clamp Y do zakresu otworu, żeby punkt nie uciekł poza kolumnę
    const maxLayerY = boundaries[boundaries.length - 1].y;
    const clampedY = Math.max(topY, Math.min(maxLayerY, svgY));
    const depthM = (clampedY - topY) / pixelsPerMeter;

    return {
      boreholeId: closestBorehole.id,
      boreholeIndex: closestIndex,
      depthFromTop: depthM,
      x: bhX,
      y: clampedY,
      edge: false
    };
  };

  /**
   * Oblicza aktualne SVG (x,y) dla zapisanego punktu freeDraw.
   * Snap-owany punkt: szuka otworu po boreholeId i oblicza Y krawędzi.
   * Wolny punkt: przelicza distanceM/depthM na piksele.
   */
  const resolvePointSvg = (pt) => {
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepthUtil(boreholes);

    if (pt.boreholeId) {
      // Snap-owany — szukaj otworu i oblicz aktualną pozycję krawędzi
      const bh = boreholes.find(b => b.id === pt.boreholeId);
      if (!bh) {
        // Otwór usunięty — fallback na absolutne
        return { x: 100 + (pt.distanceM || 0) * horizontalScale, y: 80 + (pt.depthM || 0) * pixelsPerMeter };
      }
      const bhX = 100 + bh.distance * horizontalScale;
      const depthOffset = (maxDepth - bh.depth) * pixelsPerMeter;
      const topY = 80 + depthOffset;

      // Wolny Y wzdłuż otworu (offset od górnej krawędzi w metrach)
      if (pt.depthFromTop != null) {
        return { x: bhX, y: topY + pt.depthFromTop * pixelsPerMeter };
      }

      // Wsteczna kompatybilność: środek warstwy (starsze punkty)
      if (pt.midLayerIndex != null) {
        let cY = topY;
        for (let i = 0; i < bh.layers.length && i < pt.midLayerIndex; i++) {
          cY += bh.layers[i].thickness * pixelsPerMeter;
        }
        const layer = bh.layers[pt.midLayerIndex];
        if (layer) cY += (layer.thickness * pixelsPerMeter) / 2;
        return { x: bhX, y: cY };
      }

      // Krawędź warstwy
      if (pt.boundaryIndex === 0) return { x: bhX, y: topY };
      let cY = topY;
      for (let i = 0; i < bh.layers.length && i < pt.boundaryIndex; i++) {
        cY += bh.layers[i].thickness * pixelsPerMeter;
      }
      return { x: bhX, y: cY };
    }

    // Wolny punkt — absolutne metry → piksele
    return {
      x: 100 + pt.distanceM * horizontalScale,
      y: 80 + pt.depthM * pixelsPerMeter
    };
  };

  /**
   * Tworzy połączenie freeDraw z tablicy punktów (polygon).
   * Snap-owane punkty: {boreholeId, boundaryIndex}
   * Wolne punkty: {distanceM, depthM}
   */
  /**
   * Konwertuje punkt freeDraw na absolutne metry (distanceM, depthM).
   */
  const pointToMeters = (pt) => {
    const pixelsPerMeter = scale / 10;
    if (pt.boreholeId) {
      const svg = resolvePointSvg(pt);
      return { distanceM: (svg.x - 100) / horizontalScale, depthM: (svg.y - 80) / pixelsPerMeter };
    }
    return { distanceM: pt.distanceM, depthM: pt.depthM };
  };

  /**
   * Sampluje krzywą kwadratową Bézier (Q) na n punktów.
   * start, control, end — obiekty {x, y} w metrach.
   */
  const sampleQuadBezier = (start, control, end, n = 10) => {
    const points = [];
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const t1 = 1 - t;
      const x = t1 * t1 * start[0] + 2 * t1 * t * control[0] + t * t * end[0];
      const y = t1 * t1 * start[1] + 2 * t1 * t * control[1] + t * t * end[1];
      points.push([x, y]);
    }
    return points;
  };

  /**
   * Konwertuje tablicę punktów freeDraw na ring polygon-clipping: [[x,y], [x,y], ...]
   * Jeśli polygon ma controlPoints (krzywe Bézier), sampluje krzywe na dodatkowe punkty.
   */
  const pointsToRing = (pts, controlPoints) => {
    const metersPoints = pts.map(p => {
      const m = pointToMeters(p);
      return [m.distanceM, m.depthM];
    });

    if (!controlPoints || Object.keys(controlPoints).length === 0) {
      return metersPoints;
    }

    // Buduj ring z samplowaniem krzywych Bézier
    const ring = [];
    const n = metersPoints.length;
    for (let i = 0; i < n; i++) {
      ring.push(metersPoints[i]); // dodaj wierzchołek
      const cp = controlPoints[`edge${i}`];
      if (cp) {
        const start = metersPoints[i];
        const end = metersPoints[(i + 1) % n];
        // ControlPoint jako offset od środka krawędzi
        const midX = (start[0] + end[0]) / 2;
        const midY = (start[1] + end[1]) / 2;
        const control = [midX + (cp.offsetDistanceM || 0), midY + (cp.offsetDepthM || 0)];
        // Sampluj krzywą Q — pomijamy start (już dodany) i end (dodamy w następnej iteracji)
        const sampled = sampleQuadBezier(start, control, end, 10);
        // Dodaj punkty pośrednie (bez ostatniego — to będzie następny wierzchołek)
        for (let j = 0; j < sampled.length - 1; j++) {
          ring.push(sampled[j]);
        }
      }
    }
    return ring;
  };

  /**
   * Konwertuje ring polygon-clipping z powrotem na punkty freeDraw.
   * Próbuje dopasować wynikowe punkty do oryginalnych referencji snap (boreholeId+boundaryIndex),
   * żeby polygon nadal śledził otwory po zmianie ich pozycji.
   */
  const ringToPoints = (ring, originalPoints) => {
    // Zbuduj lookup oryginalnych punktów w metrach → referencja
    const origLookup = [];
    if (originalPoints) {
      for (const op of originalPoints) {
        const m = pointToMeters(op);
        origLookup.push({ distanceM: m.distanceM, depthM: m.depthM, orig: op });
      }
    }

    const SNAP_TOLERANCE = 0.01; // tolerancja w metrach

    return ring.map(([distanceM, depthM]) => {
      // Szukaj oryginalnego punktu blisko tego wyniku
      for (const ol of origLookup) {
        if (Math.abs(ol.distanceM - distanceM) < SNAP_TOLERANCE && Math.abs(ol.depthM - depthM) < SNAP_TOLERANCE) {
          // Punkt pasuje — zachowaj oryginalną referencję
          if (ol.orig.boreholeId) {
            return { boreholeId: ol.orig.boreholeId, boundaryIndex: ol.orig.boundaryIndex };
          }
        }
      }
      return { distanceM, depthM };
    });
  };

  /**
   * Snap do wierzchołka lub krawędzi innego freeDraw polygonu.
   * Priorytet: wierzchołek > krawędź (kontur).
   * excludeConnId — id polygonu do pominięcia (edytowany).
   */
  const VERTEX_SNAP_TOLERANCE_PX = 15;
  const EDGE_SNAP_TOLERANCE_PX = 12;

  /**
   * Najbliższy punkt na odcinku AB od punktu P. Zwraca {x, y, dist}.
   */
  const closestPointOnSegment = (px, py, ax, ay, bx, by) => {
    const abx = bx - ax, aby = by - ay;
    const apx = px - ax, apy = py - ay;
    const ab2 = abx * abx + aby * aby;
    if (ab2 === 0) return { x: ax, y: ay, dist: Math.sqrt(apx * apx + apy * apy) };
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * abx, cy = ay + t * aby;
    const dx = px - cx, dy = py - cy;
    return { x: cx, y: cy, dist: Math.sqrt(dx * dx + dy * dy) };
  };

  const snapToFreeDrawVertex = (svgX, svgY, excludeConnId) => {
    if (!freeDrawSnapVertices && !snapToEdge) return null;

    let closest = null;
    let closestDist = VERTEX_SNAP_TOLERANCE_PX;
    const pixelsPerMeter = scale / 10;

    const freeDrawConns = manualConnections.filter(mc =>
      mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3 && mc.id !== excludeConnId
    );

    // 1. Snap do wierzchołków (wyższy priorytet)
    for (const mc of freeDrawConns) {
      for (const pt of mc.points) {
        const svg = resolvePointSvg(pt);
        const dx = svg.x - svgX;
        const dy = svg.y - svgY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < closestDist) {
          closestDist = dist;
          closest = {
            distanceM: (svg.x - 100) / horizontalScale,
            depthM: (svg.y - 80) / pixelsPerMeter,
            x: svg.x,
            y: svg.y,
            snappedVertex: true
          };
        }
      }
    }
    if (closest) return closest;

    // 2. Snap do krawędzi (konturów) — najbliższy punkt na odcinku
    let closestEdge = null;
    let closestEdgeDist = EDGE_SNAP_TOLERANCE_PX;

    for (const mc of freeDrawConns) {
      const pts = mc.points.map(p => resolvePointSvg(p));
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        const cp = closestPointOnSegment(svgX, svgY, a.x, a.y, b.x, b.y);
        if (cp.dist < closestEdgeDist) {
          closestEdgeDist = cp.dist;
          closestEdge = {
            distanceM: (cp.x - 100) / horizontalScale,
            depthM: (cp.y - 80) / pixelsPerMeter,
            x: cp.x,
            y: cp.y,
            snappedEdge: true
          };
        }
      }
    }
    return closestEdge;
  };

  /**
   * Rozszerzona logika snap: najpierw otwory, potem wierzchołki freeDraw.
   */
  const snapPoint = (svgX, svgY, excludeConnId) => {
    const pixelsPerMeter = scale / 10;
    // 1. Snap do otworu: X zawsze na linii otworu, Y do krawędzi jeśli blisko, inaczej wolny
    const snappedBorehole = snapToLayerBoundary(svgX, svgY);
    if (snappedBorehole) {
      const result = {
        boreholeId: snappedBorehole.boreholeId,
        boreholeIndex: snappedBorehole.boreholeIndex,
        x: snappedBorehole.x,
        y: snappedBorehole.y,
        edge: snappedBorehole.edge,
      };
      if (snappedBorehole.boundaryIndex != null) result.boundaryIndex = snappedBorehole.boundaryIndex;
      if (snappedBorehole.depthFromTop != null) result.depthFromTop = snappedBorehole.depthFromTop;
      return result;
    }
    // 2. Snap do wierzchołków innych freeDraw polygonów
    const snappedVertex = snapToFreeDrawVertex(svgX, svgY, excludeConnId);
    if (snappedVertex) {
      return snappedVertex;
    }
    // 3. Wolny punkt — opcjonalnie zaokrąglony do siatki w metrach
    let distanceM = (svgX - 100) / horizontalScale;
    let depthM = (svgY - 80) / pixelsPerMeter;
    let finalX = svgX;
    let finalY = svgY;
    if (snapToGrid && gridSize > 0) {
      distanceM = Math.round(distanceM / gridSize) * gridSize;
      depthM = Math.round(depthM / gridSize) * gridSize;
      finalX = 100 + distanceM * horizontalScale;
      finalY = 80 + depthM * pixelsPerMeter;
    }
    return {
      distanceM,
      depthM,
      x: finalX,
      y: finalY,
    };
  };

  const createFreeDrawConnection = (points) => {
    if (points.length < 3) return;

    const newId = Math.max(0, ...manualConnections.map(mc => mc.id || 0)) + 1;

    const savedPoints = points.map(p => {
      if (p.boreholeId) {
        return { boreholeId: p.boreholeId, boundaryIndex: p.boundaryIndex };
      }
      return { distanceM: p.distanceM, depthM: p.depthM };
    });

    const newConn = {
      id: newId,
      fromLayerId: 'freeDraw',
      toLayerId: 'freeDraw',
      color: `hsl(${Math.floor(Math.random() * 360)}, 60%, 65%)`,
      points: savedPoints,
      createdAt: Date.now()
    };

    const newRing = pointsToRing(savedPoints);

    setManualConnections(prev => {
      const existingFreeDraws = prev.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3);

      let idCounter = newId;
      const allocId = () => ++idCounter;

      if (freeDrawClipMode === 'clipNew') {
        // TRYB A: Starsze mają priorytet — przytnij nowy polygon
        let clippedNewPoly = [[newRing]];

        for (const mc of existingFreeDraws) {
          try {
            const oldRing = pointsToRing(mc.points, mc.controlPoints);
            clippedNewPoly = polygonClipping.difference(clippedNewPoly, [[oldRing]]);
            if (!clippedNewPoly || clippedNewPoly.length === 0) return prev;
          } catch { /* kontynuuj */ }
        }

        const newConns = clippedNewPoly.map((poly, idx) => {
          const pts = ringToPoints(poly[0], savedPoints);
          if (pts.length < 3) return null;
          return { ...newConn, id: idx === 0 ? newConn.id : allocId(), points: pts };
        }).filter(Boolean);
        if (newConns.length === 0) return prev;
        return [...prev, ...newConns];

      } else {
        // TRYB B: Nowsza nadpisuje starsze — przytnij starsze polygony.
        // Gdy nowy polygon dzieli stary na N rozłącznych fragmentów → każdy zostaje jako osobny mc.
        const newPoly = [[newRing]];
        const updated = prev.flatMap(mc => {
          if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length < 3) return [mc];
          try {
            const oldRing = pointsToRing(mc.points, mc.controlPoints);
            const result = polygonClipping.difference([[oldRing]], newPoly);
            if (!result || result.length === 0) return []; // stary całkowicie nadpisany
            return result.map((poly, idx) => {
              const pts = ringToPoints(poly[0], mc.points);
              if (pts.length < 3) return null;
              return {
                ...mc,
                id: idx === 0 ? mc.id : allocId(),
                points: pts,
                controlPoints: {}
              };
            }).filter(Boolean);
          } catch { return [mc]; }
        });
        return [...updated, newConn];
      }
    });
    setConnectLayers(true);
  };

  /**
   * Dzieli polygon freeDraw linią (dwa punkty w SVG) na N polygonów.
   * "Cienkie ostrze": buduje wąski prostokąt (grubość EPSILON) wzdłuż wydłużonej linii cięcia
   * i wykonuje difference(polygon, blade). Wszystkie powstałe kawałki stają się osobnymi polygonami.
   * Ten sam mechanizm co splitFreeDrawConnectionsByPolyline — działa dla wklęsłych polygonów
   * i nie cierpi na problemy degeneracji orientacji ringa, które miało wcześniejsze podejście
   * z dwoma half-plane'ami (środkowe fragmenty wklęsłych polygonów ginęły).
   */
  const splitFreeDrawConnection = (connId, svgP1, svgP2) => {
    const pixelsPerMeter = scale / 10;
    const mc = manualConnections.find(m => m.id === connId);
    if (!mc || !mc.points || mc.points.length < 3) return;

    // SVG → metry
    const p1 = { x: (svgP1.x - 100) / horizontalScale, y: (svgP1.y - 80) / pixelsPerMeter };
    const p2 = { x: (svgP2.x - 100) / horizontalScale, y: (svgP2.y - 80) / pixelsPerMeter };

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.001) return;

    // Wydłuż linię o EXT m w obie strony — żeby na pewno przebiła polygon na wylot.
    // Mała wartość (1000 m) — duże wartości typu 100000 generowały numeryczne artefakty
    // w polygon-clipping (sprawdzone w splitFreeDrawConnectionsByPolyline).
    const EXT = 1000;
    const ux = dx / len, uy = dy / len;
    const ax = p1.x - ux * EXT, ay = p1.y - uy * EXT;
    const bx = p2.x + ux * EXT, by = p2.y + uy * EXT;

    // Cienkie ostrze (prostokąt wzdłuż wydłużonej linii): normalna × EPSILON w obie strony.
    const EPSILON = 0.01; // 1 cm grubość ostrza — niewidoczna luka po cięciu
    const nx = -uy, ny = ux;
    const blade = [[[
      [ax + nx * EPSILON, ay + ny * EPSILON],
      [bx + nx * EPSILON, by + ny * EPSILON],
      [bx - nx * EPSILON, by - ny * EPSILON],
      [ax - nx * EPSILON, ay - ny * EPSILON],
      [ax + nx * EPSILON, ay + ny * EPSILON]
    ]]];

    const oldRing = pointsToRing(mc.points, mc.controlPoints);

    let diffResult;
    try {
      diffResult = polygonClipping.difference([[oldRing]], blade);
    } catch {
      return;
    }
    if (!diffResult || diffResult.length === 0) return;

    const calcArea = (ring) => Math.abs(ring.reduce((sum, pt, i, arr) => {
      const next = arr[(i + 1) % arr.length];
      return sum + (pt[0] * next[1] - next[0] * pt[1]);
    }, 0)) / 2;

    const oldArea = calcArea(oldRing);
    const minArea = Math.max(1e-5, oldArea * 1e-5);

    // Zbierz wszystkie kawałki (z odrzuceniem mikro-fragmentów wzdłuż ostrza).
    // Wszystkie wierzchołki nowych kawałków zapisywane jako WOLNE {distanceM, depthM} —
    // bez snap-mapowania na boreholeId. Powód: ostrze ma grubość EPSILON = 0.01 m,
    // a ringToPoints ma SNAP_TOLERANCE = 0.01 m. Po cięciu polygonu "przyklejonego do otworu"
    // powstawały zduplikowane referencje na ten sam {boreholeId, boundaryIndex}, co skutkowało
    // degenerowanymi krawędziami i wizualnymi lukami. Konsystentne z applyTerrainShapeToLayers
    // (15.05.2026) — urealnione warstwy też mają wszystkie wolne punkty.
    const pieces = [];
    for (const poly of diffResult) {
      const outer = poly[0];
      if (!outer || outer.length < 4) continue;
      if (calcArea(outer) < minArea) continue;
      const closed = outer[0][0] === outer[outer.length - 1][0] && outer[0][1] === outer[outer.length - 1][1];
      const ring = closed ? outer.slice(0, -1) : outer;
      if (ring.length < 3) continue;
      const pts = ring.map(([distanceM, depthM]) => ({ distanceM, depthM }));
      pieces.push(pts);
    }

    if (pieces.length < 2) return;

    setManualConnections(prev => {
      const baseId = Math.max(0, ...prev.map(m => m.id || 0)) + 1;
      const without = prev.filter(m => m.id !== connId);
      const now = Date.now();
      const newPieces = pieces.map((pts, i) => ({
        ...mc,
        id: baseId + i,
        points: pts,
        controlPoints: {},
        labelPos: undefined,
        createdAt: now + i
      }));
      return [...without, ...newPieces];
    });

    setSplitMode(false);
    setSplittingConnId(null);
    setSplitLinePoints([]);
    setSplitLinePreview(null);
  };

  /**
   * Klik w trybie split — dodaje punkt, po 2 punktach wykonuje podział.
   */
  const handleSplitClick = (svgX, svgY) => {
    if (!splitMode || !splittingConnId) return;
    setSplitLinePoints(prev => {
      const next = [...prev, { x: svgX, y: svgY }];
      if (next.length >= 2) {
        splitFreeDrawConnection(splittingConnId, next[0], next[1]);
        return [];
      }
      return next;
    });
  };

  const cancelSplit = () => {
    setSplitMode(false);
    setSplittingConnId(null);
    setSplitLinePoints([]);
    setSplitLinePreview(null);
  };

  /**
   * Dzieli WSZYSTKIE freeDraw polygony, które polilinia (≥2 punkty w SVG) przecina.
   *
   * Algorytm „cienkie ostrze": dla każdego segmentu polilinii buduje wąski prostokąt
   * (grubość EPSILON), wszystkie łączy w jedno multipolygonowe ostrze przez `union`,
   * potem dla każdego mc liczy `difference(polygon, blade)`. Jeśli wynik ma ≥2 kawałki —
   * cięcie się udało. To unika problemu z konstrukcją bisektorów normalnych przy ostrych
   * zakrętach polilinii (poprzednia wersja generowała samoprzecinające się ringi bufora).
   */
  const splitFreeDrawConnectionsByPolyline = (svgPts) => {
    if (!svgPts || svgPts.length < 2) return { cut: 0, skipped: 0, autoConnectSkipped: 0 };
    const pixelsPerMeter = scale / 10;
    const stats = { cut: 0, skipped: 0, autoConnectSkipped: 0 };

    // SVG → metry + dedupe sąsiednich duplikatów (browser przy dblklik strzela click+dblclick,
    // co dorzucało punkt w tym samym miejscu co poprzedni → lenN≈0 → wczesny return)
    const rawPolyM = svgPts.map(p => ({
      x: (p.x - 100) / horizontalScale,
      y: (p.y - 80) / pixelsPerMeter,
    }));
    const polyM = [];
    const DEDUP_EPS = 1e-4; // 0.1 mm w metrach
    for (const p of rawPolyM) {
      const last = polyM[polyM.length - 1];
      if (!last || Math.abs(last.x - p.x) > DEDUP_EPS || Math.abs(last.y - p.y) > DEDUP_EPS) {
        polyM.push(p);
      }
    }
    if (polyM.length < 2) return stats;

    // Wydłuż końce o EXT — żeby polilinia przebijała każdy polygon na wylot nawet jeśli user
    // skończył klikanie wewnątrz warstwy. EXT mniejsze niż w starym algorytmie (1000 m wystarczy
    // dla typowego przekroju), żeby polygon-clipping nie miał problemów numerycznych.
    const EXT = 1000;
    const EPSILON = 0.05; // grubość ostrza w metrach (5 cm — wciąż pomijalna wizualnie, ale stabilniejsza dla polygon-clipping)

    const first = polyM[0], second = polyM[1];
    const dx0 = second.x - first.x, dy0 = second.y - first.y;
    const len0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
    if (len0 < 1e-6) return stats;
    const extStart = { x: first.x - (dx0 / len0) * EXT, y: first.y - (dy0 / len0) * EXT };

    const lastP = polyM[polyM.length - 1], prevP = polyM[polyM.length - 2];
    const dxN = lastP.x - prevP.x, dyN = lastP.y - prevP.y;
    const lenN = Math.sqrt(dxN * dxN + dyN * dyN);
    if (lenN < 1e-6) return stats;
    const extEnd = { x: lastP.x + (dxN / lenN) * EXT, y: lastP.y + (dyN / lenN) * EXT };

    const ext = [extStart, ...polyM, extEnd];

    // Buduj ostrze: union prostokątów per-segment (każdy ±EPSILON w kierunku normalnej)
    let blade = null;
    for (let i = 0; i < ext.length - 1; i++) {
      const p1 = ext[i], p2 = ext[i + 1];
      const dx = p2.x - p1.x, dy = p2.y - p1.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 1e-6) continue;
      const nx = (-dy / len) * EPSILON;
      const ny = (dx / len) * EPSILON;
      const rect = [[
        [p1.x - nx, p1.y - ny],
        [p2.x - nx, p2.y - ny],
        [p2.x + nx, p2.y + ny],
        [p1.x + nx, p1.y + ny],
        [p1.x - nx, p1.y - ny],
      ]];
      if (blade == null) {
        blade = [rect];
      } else {
        try {
          blade = polygonClipping.union(blade, [rect]);
        } catch {
          // Jeśli union zawiedzie dla danego segmentu, pomijamy go — reszta ostrza wystarczy
        }
      }
    }
    if (!blade || blade.length === 0) return stats;

    const calcArea = (ring) => Math.abs(ring.reduce((sum, pt, i, arr) => {
      const next = arr[(i + 1) % arr.length];
      return sum + (pt[0] * next[1] - next[0] * pt[1]);
    }, 0)) / 2;

    // Liczymy synchronicznie z bieżącego `manualConnections` (closure), żeby stats były
    // wypełnione zanim wrócimy z funkcji. setManualConnections dostaje gotową tablicę.
    const prev = manualConnections;
    let idCounter = Math.max(0, ...prev.map(m => m.id || 0));
    const out = [];

    for (const mc of prev) {
      if (mc.fromLayerId !== 'freeDraw') {
        if (mc.fromLayerId && mc.fromLayerId !== 'freeDraw') stats.autoConnectSkipped += 1;
        out.push(mc);
        continue;
      }
      if (!mc.points || mc.points.length < 3) {
        out.push(mc);
        continue;
      }

      const oldRing = pointsToRing(mc.points, mc.controlPoints);
      const oldArea = calcArea(oldRing);

      let diffResult;
      try {
        diffResult = polygonClipping.difference([[oldRing]], blade);
      } catch {
        stats.skipped += 1;
        out.push(mc);
        continue;
      }

      // Pusto = ostrze pokryło całość (nie powinno się zdarzyć przy EPSILON 5cm) → zachowaj oryginał
      if (!diffResult || diffResult.length === 0) {
        stats.skipped += 1;
        out.push(mc);
        continue;
      }

      // Zbuduj kawałki, odrzuć mikro-fragmenty (artefakty numeryczne tuż obok ostrza)
      // Bardzo niski floor — chcemy zachować nawet 1 mm² kawałki, byleby nie były zerem.
      const minArea = Math.max(1e-5, oldArea * 1e-5);
      const pieces = [];
      for (const poly of diffResult) {
        const outer = poly[0];
        if (!outer || outer.length < 4) continue;
        if (calcArea(outer) < minArea) continue;
        const closed = outer[0][0] === outer[outer.length - 1][0] && outer[0][1] === outer[outer.length - 1][1];
        const ring = closed ? outer.slice(0, -1) : outer;
        if (ring.length < 3) continue;
        pieces.push(ringToPoints(ring, mc.points));
      }

      // Brak cięcia (1 kawałek lub mniej po filtrowaniu) → zostaw oryginalny mc nietknięty
      if (pieces.length < 2) {
        stats.skipped += 1;
        out.push(mc);
        continue;
      }

      stats.cut += 1;
      for (const pts of pieces) {
        idCounter += 1;
        out.push({
          ...mc,
          id: idCounter,
          points: pts,
          controlPoints: {},
          labelPos: undefined, // pozwól polylabel przeliczyć dla nowych kawałków
          createdAt: Date.now() + idCounter,
        });
      }
    }

    setManualConnections(out);
    return stats;
  };

  const handlePolylineSplitClick = (svgX, svgY) => {
    if (!polylineSplitMode) return;
    setPolylineSplitPoints(prev => [...prev, { x: svgX, y: svgY }]);
  };

  const finishPolylineSplit = () => {
    if (!polylineSplitMode) return;
    if (polylineSplitPoints.length >= 2) {
      const stats = splitFreeDrawConnectionsByPolyline(polylineSplitPoints);
      setPolylineSplitStatus({ ...stats, ts: Date.now() });
    }
    setPolylineSplitMode(false);
    setPolylineSplitPoints([]);
    setPolylineSplitPreview(null);
  };

  const cancelPolylineSplit = () => {
    setPolylineSplitMode(false);
    setPolylineSplitPoints([]);
    setPolylineSplitPreview(null);
  };

  /**
   * Tworzy nowy polygon freeDraw przycięty do obszaru polygonu-rodzica.
   * Używane dla soczewek rysowanych wewnątrz istniejącej warstwy.
   */
  const createLensInLayer = (points, parentConnId) => {
    if (points.length < 3) return;
    const parent = manualConnections.find(mc => mc.id === parentConnId);
    if (!parent) return;

    const savedPoints = points.map(p => {
      if (p.boreholeId) {
        const out = { boreholeId: p.boreholeId };
        if (p.boundaryIndex != null) out.boundaryIndex = p.boundaryIndex;
        if (p.depthFromTop != null) out.depthFromTop = p.depthFromTop;
        return out;
      }
      return { distanceM: p.distanceM, depthM: p.depthM };
    });

    const newRing = pointsToRing(savedPoints);
    const parentRing = pointsToRing(parent.points, parent.controlPoints);

    let clipped;
    try {
      clipped = polygonClipping.intersection([[newRing]], [[parentRing]]);
    } catch {
      return;
    }
    if (!clipped || clipped.length === 0) return;

    const calcArea = (ring) => Math.abs(ring.reduce((sum, pt, i, arr) => {
      const next = arr[(i + 1) % arr.length];
      return sum + (pt[0] * next[1] - next[0] * pt[1]);
    }, 0)) / 2;
    const getLargest = (mp) => mp.reduce((best, poly) =>
      calcArea(poly[0]) > calcArea(best[0]) ? poly : best, mp[0]);

    const clippedPts = ringToPoints(getLargest(clipped)[0], savedPoints);
    if (clippedPts.length < 3) return;

    const newId = Math.max(0, ...manualConnections.map(mc => mc.id || 0)) + 1;
    const newConn = {
      id: newId,
      fromLayerId: 'freeDraw',
      toLayerId: 'freeDraw',
      color: `hsl(${Math.floor(Math.random() * 360)}, 70%, 55%)`,
      points: clippedPts,
      createdAt: Date.now(),
    };

    // freeDrawClipMode jak w normalnym createFreeDrawConnection — parent też wlicza się
    // jako konkurent (sticky lock klipuje do parenta przez intersection, a potem clipMode
    // decyduje o z-order względem WSZYSTKICH starszych freeDraw, łącznie z parentem):
    //   clipNew ("Pod") — odejmij wszystkich starszych od nowego (nowy w środku parenta → pusty)
    //   clipOld ("Na")  — przytnij starszych przez nowy (parent dostaje dziurę, nowy na wierzchu)
    const clippedNewRing = pointsToRing(clippedPts);
    setManualConnections(prev => {
      const olderFreeDraws = prev.filter(mc =>
        mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3
      );

      let idCounter = newId;
      const allocId = () => ++idCounter;

      if (freeDrawClipMode === 'clipNew') {
        let clippedPoly = [[clippedNewRing]];
        for (const old of olderFreeDraws) {
          try {
            const oldRing = pointsToRing(old.points, old.controlPoints);
            clippedPoly = polygonClipping.difference(clippedPoly, [[oldRing]]);
            if (!clippedPoly || clippedPoly.length === 0) return prev;
          } catch { /* kontynuuj */ }
        }
        const newConns = clippedPoly.map((poly, idx) => {
          const pts = ringToPoints(poly[0], clippedPts);
          if (pts.length < 3) return null;
          return { ...newConn, id: idx === 0 ? newConn.id : allocId(), points: pts };
        }).filter(Boolean);
        if (newConns.length === 0) return prev;
        return [...prev, ...newConns];
      } else {
        const newPoly = [[clippedNewRing]];
        const updated = prev.flatMap(mc => {
          if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length < 3) return [mc];
          try {
            const oldRing = pointsToRing(mc.points, mc.controlPoints);
            const result = polygonClipping.difference([[oldRing]], newPoly);
            if (!result || result.length === 0) return [];
            return result.map((poly, idx) => {
              const pts = ringToPoints(poly[0], mc.points);
              if (pts.length < 3) return null;
              return {
                ...mc,
                id: idx === 0 ? mc.id : allocId(),
                points: pts,
                controlPoints: {},
              };
            }).filter(Boolean);
          } catch { return [mc]; }
        });
        return [...updated, newConn];
      }
    });
    setConnectLayers(true);
    // W trybie sticky lock zostajemy w tej warstwie — pozwala rysować wiele kształtów po kolei
    if (!stickyLayerLock) {
      setLensInLayerMode(false);
      setLensInLayerParentId(null);
    }
    setFreeDrawPoints([]);
    setFreeDrawPreview(null);
  };

  const cancelLensInLayer = () => {
    setLensInLayerMode(false);
    setLensInLayerParentId(null);
    setStickyLayerLock(false);
    setFreeDrawPoints([]);
    setFreeDrawPreview(null);
  };

  /**
   * Aktualizuje wybrane właściwości polygonu freeDraw (name, color, pattern).
   * Przyjmuje obiekt z wybranymi polami do nadpisania.
   */
  const updateFreeDrawProps = (connId, patch) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      return { ...mc, ...patch };
    }));
  };

  /**
   * Kopiuje właściwości (name, color, pattern) polygonu freeDraw do bufora.
   */
  const copyFreeDrawProps = (connId) => {
    const mc = manualConnections.find(m => m.id === connId);
    if (!mc) return;
    setCopiedFreeDrawProps({
      name: mc.name || '',
      color: mc.color || '#CCCCCC',
      pattern: mc.pattern || 'none',
    });
  };

  /**
   * Wkleja zapamiętane właściwości do innego polygonu.
   */
  const pasteFreeDrawProps = (connId) => {
    if (!copiedFreeDrawProps) return;
    updateFreeDrawProps(connId, { ...copiedFreeDrawProps });
  };

  /**
   * Wycina dziurę z polygonu (points to nowy kształt — obszar do usunięcia).
   * Jeśli dziura dzieli polygon na kilka części — zostają wszystkie jako osobne polygony.
   */
  const cutHoleInFreeDraw = (points, parentConnId) => {
    if (points.length < 3) return;
    const parent = manualConnections.find(mc => mc.id === parentConnId);
    if (!parent) return;

    const savedPoints = points.map(p => {
      if (p.boreholeId) {
        const out = { boreholeId: p.boreholeId };
        if (p.boundaryIndex != null) out.boundaryIndex = p.boundaryIndex;
        if (p.depthFromTop != null) out.depthFromTop = p.depthFromTop;
        return out;
      }
      return { distanceM: p.distanceM, depthM: p.depthM };
    });

    const holeRing = pointsToRing(savedPoints);
    const parentOuterRing = pointsToRing(parent.points, parent.controlPoints);
    // Uwzględnij istniejące dziury w parencie (po wcześniejszych cut-hole) jako kolejne ringi.
    const parentRings = [parentOuterRing, ...((parent.holes || []).map(h => pointsToRing(h)))];

    let result;
    try {
      result = polygonClipping.difference([parentRings], [[holeRing]]);
    } catch {
      return;
    }
    if (!result || result.length === 0) return;

    setManualConnections(prev => {
      const without = prev.filter(m => m.id !== parentConnId);
      let nextId = Math.max(0, ...prev.map(m => m.id || 0));
      const newConns = result.map((poly, idx) => {
        nextId += 1;
        const outerPts = ringToPoints(poly[0], parent.points);
        if (outerPts.length < 3) return null;
        // poly[1..] = dziury (wewnętrzne ringi). Zostają w mc.holes.
        const holes = poly.slice(1)
          .map(ring => ringToPoints(ring, parent.points))
          .filter(h => h.length >= 3);
        return {
          ...parent,
          id: nextId,
          points: outerPts,
          holes: holes.length > 0 ? holes : undefined,
          controlPoints: {},
          createdAt: Date.now() + idx,
        };
      }).filter(Boolean);
      return [...without, ...newConns];
    });

    setCutHoleMode(false);
    setCutHoleParentId(null);
    setFreeDrawPoints([]);
    setFreeDrawPreview(null);
  };

  const cancelCutHole = () => {
    setCutHoleMode(false);
    setCutHoleParentId(null);
    setFreeDrawPoints([]);
    setFreeDrawPreview(null);
  };

  /**
   * Wygładza krawędzie polygonu — dodaje punkty kontrolne Béziera dla każdej krawędzi
   * z niewielkim offsetem prostopadłym, żeby zakręty były płynne.
   */
  const smoothFreeDrawEdges = (connId) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId || !mc.points || mc.points.length < 3) return mc;
      // Wylicz punkty w metrach i offset prostopadły dla każdej krawędzi
      const ptsM = mc.points.map(p => pointToMeters(p));
      const n = ptsM.length;
      const controlPoints = {};
      for (let i = 0; i < n; i++) {
        const curr = ptsM[i];
        const next = ptsM[(i + 1) % n];
        const dx = next.distanceM - curr.distanceM;
        const dy = next.depthM - curr.depthM;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 0.001) continue;
        // Offset prostopadły — 15% długości krawędzi (daje widoczne zaokrąglenie)
        const OFFSET_FRACTION = 0.15;
        // Prostopadły znak: sprawdź w którą stronę jest centroid żeby zaokrąglenie było na zewnątrz
        const cx = ptsM.reduce((s, p) => s + p.distanceM, 0) / n;
        const cy = ptsM.reduce((s, p) => s + p.depthM, 0) / n;
        const midX = (curr.distanceM + next.distanceM) / 2;
        const midY = (curr.depthM + next.depthM) / 2;
        // Normalna (znormalizowana)
        const nx = -dy / len;
        const ny = dx / len;
        // Skieruj normalną na zewnątrz (od centroidu)
        const outward = ((midX - cx) * nx + (midY - cy) * ny) >= 0 ? 1 : -1;
        controlPoints[`edge${i}`] = {
          offsetDistanceM: nx * len * OFFSET_FRACTION * outward,
          offsetDepthM: ny * len * OFFSET_FRACTION * outward,
        };
      }
      return { ...mc, controlPoints };
    }));
  };

  /**
   * Scala dwa polygony freeDraw poprzez geometryczną unię.
   * Zachowuje właściwości pierwszego polygonu (kolor, nazwa, wzór, warstwa bazowa).
   * Stykające się polygony → jeden scalony polygon.
   * Rozłączne polygony → kilka osobnych polygonów (każda część zachowana z ujednoliconym wyglądem).
   * Zwraca id pierwszego nowego polygonu lub null gdy operacja się nie powiodła.
   */
  const mergeFreeDrawPolygons = (idA, idB) => {
    if (idA == null || idB == null || idA === idB) return null;

    const connA = manualConnections.find(mc => mc.id === idA && mc.fromLayerId === 'freeDraw');
    const connB = manualConnections.find(mc => mc.id === idB && mc.fromLayerId === 'freeDraw');
    if (!connA || !connB || !connA.points || !connB.points || connA.points.length < 3 || connB.points.length < 3) return null;

    const ringA = pointsToRing(connA.points, connA.controlPoints);
    const ringB = pointsToRing(connB.points, connB.controlPoints);

    // polygon-clipping dla polygonów stykających się tylko wzdłuż wspólnej krawędzi
    // (typowy przypadek freeDraw po użyciu „Tnij polilinią": dwa kawałki dzielące
    // pionową krawędź na osi otworu) zwraca dwa odrębne polygony zamiast scalonego.
    // Inflacja od centroidu wymusza overlap → union rozpoznaje styk i scala.
    // Próbujemy kaskadowo coraz większy współczynnik, aż dostaniemy 1 polygon.
    const inflate = (ring, factor) => {
      const n = ring.length;
      let cx = 0, cy = 0;
      for (const p of ring) { cx += p[0]; cy += p[1]; }
      cx /= n; cy /= n;
      return ring.map(([x, y]) => [cx + (x - cx) * factor, cy + (y - cy) * factor]);
    };

    let unionResult;
    try {
      unionResult = polygonClipping.union([[ringA]], [[ringB]]);
      if (unionResult && unionResult.length > 1) {
        // Cascade inflation. 1.001 = 0.1%, 1.01 = 1%, 1.05 = 5%.
        // Dla typowego polygonu 20 m: 0.1% = 2 cm, 1% = 20 cm, 5% = 1 m.
        // Pierwszy wynik, który zwraca jeden polygon, wygrywa.
        for (const factor of [1.001, 1.005, 1.01, 1.02, 1.05]) {
          const ringAi = inflate(ringA, factor);
          const ringBi = inflate(ringB, factor);
          const retry = polygonClipping.union([[ringAi]], [[ringBi]]);
          if (retry && retry.length === 1) {
            unionResult = retry;
            break;
          }
          if (retry && retry.length < unionResult.length) {
            unionResult = retry;
          }
        }
      }
    } catch (err) {
      console.error('[mergeFreeDrawPolygons] union failed:', err);
      return null;
    }
    if (!unionResult || unionResult.length === 0) return null;

    // Konwersja każdego polygonu z unii z powrotem na punkty.
    // Dla rozłącznych polygonów union zwróci kilka — każdy zachowujemy jako osobny connection.
    const baseId = Math.max(0, ...manualConnections.map(mc => mc.id || 0)) + 1;
    const baseTs = Date.now();
    const sourcePoints = [...(connA.points || []), ...(connB.points || [])];

    const newConnections = [];
    for (let i = 0; i < unionResult.length; i++) {
      const ring = unionResult[i][0];
      const pts = ringToPoints(ring, sourcePoints);
      if (!pts || pts.length < 3) continue;
      newConnections.push({
        id: baseId + i,
        fromLayerId: 'freeDraw',
        toLayerId: 'freeDraw',
        // Dziedziczymy z A (pierwszego klikniętego)
        color: connA.color,
        name: connA.name,
        pattern: connA.pattern,
        baseLayer: connA.baseLayer,
        points: pts,
        // controlPoints porzucamy — krzywe Bézier po unii nie mają jednoznacznego mapowania
        createdAt: baseTs + i,
      });
    }

    if (newConnections.length === 0) return null;

    setManualConnections(prev => [
      ...prev.filter(mc => mc.id !== idA && mc.id !== idB),
      ...newConnections,
    ]);

    return newConnections[0].id;
  };

  /**
   * Przenosi polygon na wierzch (najnowszy createdAt = rysowany ostatni).
   */
  const bringFreeDrawToFront = (connId) => {
    setManualConnections(prev => {
      const maxCreated = Math.max(0, ...prev.map(m => m.createdAt || 0));
      return prev.map(mc => mc.id === connId ? { ...mc, createdAt: maxCreated + 1 } : mc);
    });
  };

  /**
   * Przenosi polygon na spód (najstarszy createdAt).
   */
  const sendFreeDrawToBack = (connId) => {
    setManualConnections(prev => {
      const minCreated = Math.min(...prev.filter(m => m.createdAt != null).map(m => m.createdAt), Date.now());
      return prev.map(mc => mc.id === connId ? { ...mc, createdAt: minCreated - 1 } : mc);
    });
  };


  /**
   * Obsługa kliknięcia w trybie rysowania ręcznego - krzywa łamana.
   * Blisko słupka → snap, daleko → wolny punkt.
   */
  /**
   * Ortho snap: gdy snapToOrtho ON lub shift trzymany — przyciąga (svgX,svgY) do
   * linii poziomej/pionowej od ostatniego punktu freeDrawPoints. Wybiera oś o większej
   * składowej ruchu. Bez efektu gdy nie ma poprzedniego punktu.
   */
  const applyOrthoSnap = (svgX, svgY, shiftHeld) => {
    if (!(snapToOrtho || shiftHeld)) return { x: svgX, y: svgY };
    if (!freeDrawPoints || freeDrawPoints.length === 0) return { x: svgX, y: svgY };
    const last = freeDrawPoints[freeDrawPoints.length - 1];
    const lastSvg = resolvePointSvg(last);
    const dx = Math.abs(svgX - lastSvg.x);
    const dy = Math.abs(svgY - lastSvg.y);
    if (dx >= dy) return { x: svgX, y: lastSvg.y }; // pozioma — Y zachowane
    return { x: lastSvg.x, y: svgY };               // pionowa — X zachowane
  };

  const handleFreeDrawClick = (svgX, svgY, shiftHeld = false) => {
    if (!freeDrawMode && !lensInLayerMode && !cutHoleMode) return;

    const ortho = applyOrthoSnap(svgX, svgY, shiftHeld);
    svgX = ortho.x;
    svgY = ortho.y;

    const sp = snapPoint(svgX, svgY, null);
    const point = sp.boreholeId ? {
      boreholeId: sp.boreholeId,
      boreholeIndex: sp.boreholeIndex || 0,
      ...(sp.boundaryIndex != null && { boundaryIndex: sp.boundaryIndex }),
      ...(sp.depthFromTop != null && { depthFromTop: sp.depthFromTop }),
      x: sp.x, y: sp.y,
    } : {
      distanceM: sp.distanceM,
      depthM: sp.depthM,
      boreholeId: null,
      boreholeIndex: -1,
      x: sp.x, y: sp.y,
    };

    setFreeDrawPoints(prev => [...prev, point]);
  };

  /**
   * Podwójne kliknięcie zamyka kształt freeDraw.
   */
  const handleFreeDrawDoubleClick = (svgX, svgY, shiftHeld = false) => {
    if (!freeDrawMode && !lensInLayerMode && !cutHoleMode) return;
    if (freeDrawPoints.length < 2) return;

    const ortho = applyOrthoSnap(svgX, svgY, shiftHeld);
    svgX = ortho.x;
    svgY = ortho.y;

    const sp = snapPoint(svgX, svgY, null);
    const lastPoint = sp.boreholeId ? {
      boreholeId: sp.boreholeId,
      boreholeIndex: sp.boreholeIndex || 0,
      ...(sp.boundaryIndex != null && { boundaryIndex: sp.boundaryIndex }),
      ...(sp.depthFromTop != null && { depthFromTop: sp.depthFromTop }),
      x: sp.x, y: sp.y,
    } : {
      distanceM: sp.distanceM,
      depthM: sp.depthM,
      boreholeId: null,
      x: sp.x, y: sp.y,
    };

    const allPoints = [...freeDrawPoints, lastPoint];
    if (cutHoleMode && cutHoleParentId != null) {
      cutHoleInFreeDraw(allPoints, cutHoleParentId);
    } else if (lensInLayerMode && lensInLayerParentId != null) {
      // 💎 Soczewka (PPM "Narysuj soczewkę") — one-shot, wyłącza tryb po jednym
      createLensInLayer(allPoints, lensInLayerParentId);
    } else if (stickyLayerLock && lensInLayerParentId != null) {
      // 🔒 Sticky lock w trybie ✏️ Rysowanie — klipuje do parenta, mode zostaje (sticky)
      createLensInLayer(allPoints, lensInLayerParentId);
    } else {
      createFreeDrawConnection(allPoints);
    }
    setFreeDrawPoints([]);
  };

  /**
   * Usuń zaznaczony freeDraw polygon.
   */
  const deleteFreeDraw = (id) => {
    setManualConnections(prev => prev.filter(mc => mc.id !== id));
    setSelectedFreeDraw(null);
    setEditingFreeDraw(null);
  };

  /**
   * Rozpocznij przeciąganie wierzchołka freeDraw.
   */
  const handleFreeDrawPointDragStart = (e, connId, pointIndex) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingFreeDrawPoint({ connId, pointIndex });
    // Zapamiętaj kształt przed dragiem (do wykrywania luk)
    if (freeDrawFillGaps) {
      const mc = manualConnections.find(m => m.id === connId);
      if (mc && mc.points && mc.points.length >= 3) {
        preDragShapeRef.current = pointsToRing(mc.points, mc.controlPoints);
      }
    } else {
      preDragShapeRef.current = null;
    }
    lockScroll();
  };

  /**
   * Przesuń wierzchołek freeDraw (mouseMove).
   */
  const handleFreeDrawPointDragMove = (e) => {
    if (!draggingFreeDrawPoint) return;
    const { x: svgX, y: svgY } = getSvgCoordinates(e);
    const { connId, pointIndex } = draggingFreeDrawPoint;

    const sp = snapPoint(svgX, svgY, connId);

    let newPoint;
    if (sp.boreholeId) {
      newPoint = { boreholeId: sp.boreholeId, boundaryIndex: sp.boundaryIndex };
    } else {
      newPoint = { distanceM: sp.distanceM, depthM: sp.depthM };
    }

    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      const newPoints = [...mc.points];
      newPoints[pointIndex] = newPoint;
      return { ...mc, points: newPoints };
    }));
  };

  /**
   * Zakończ przeciąganie wierzchołka freeDraw.
   */
  const handleFreeDrawPointDragEnd = () => {
    if (!draggingFreeDrawPoint) return;
    const editedId = draggingFreeDrawPoint.connId;
    setDraggingFreeDrawPoint(null);
    unlockScroll();
    // Natychmiastowy clipping — przytnij sąsiednie warstwy po każdym dragu
    if (freeDrawEditVerticesMode) {
      applyFreeDrawEditClipping(editedId);
    }
  };

  /**
   * Rozpocznij przeciąganie punktu kontrolnego krawędzi freeDraw.
   */
  const handleFreeDrawControlDragStart = (e, connId, edgeIndex) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingFreeDrawControl({ connId, edgeIndex });
    lockScroll();
  };

  /**
   * Przesuń punkt kontrolny krawędzi freeDraw (mouseMove).
   */
  const handleFreeDrawControlDragMove = (e) => {
    if (!draggingFreeDrawControl) return;
    const { x: svgX, y: svgY } = getSvgCoordinates(e);
    const { connId, edgeIndex } = draggingFreeDrawControl;
    const pixelsPerMeter = scale / 10;

    // Oblicz offset od środka krawędzi (w metrach)
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      if (!mc.points || mc.points.length < 3) return mc;

      const pts = mc.points;
      const p1 = resolvePointSvg(pts[edgeIndex]);
      const p2 = resolvePointSvg(pts[(edgeIndex + 1) % pts.length]);

      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;

      const cpData = {
        offsetDistanceM: (svgX - midX) / horizontalScale,
        offsetDepthM: (svgY - midY) / pixelsPerMeter,
      };

      const controlPoints = { ...(mc.controlPoints || {}) };
      controlPoints[`edge${edgeIndex}`] = cpData;
      return { ...mc, controlPoints };
    }));
  };

  /**
   * Zakończ przeciąganie punktu kontrolnego.
   */
  const handleFreeDrawControlDragEnd = () => {
    if (!draggingFreeDrawControl) return;
    setDraggingFreeDrawControl(null);
    unlockScroll();
  };

  /**
   * Zresetuj punkt kontrolny krawędzi (powrót do prostej linii).
   */
  const resetFreeDrawControlPoint = (connId, edgeIndex) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      const controlPoints = { ...(mc.controlPoints || {}) };
      delete controlPoints[`edge${edgeIndex}`];
      return { ...mc, controlPoints };
    }));
  };

  /**
   * Rozpocznij przeciąganie całej krawędzi równolegle (oba wierzchołki razem).
   */
  const handleFreeDrawEdgeDragStart = (e, connId, edgeIndex) => {
    e.stopPropagation();
    e.preventDefault();
    const mc = manualConnections.find(m => m.id === connId);
    if (!mc || !mc.points || mc.points.length < 3) return;
    const { x: sx, y: sy } = getSvgCoordinates(e);
    const pts = mc.points;
    const p1 = resolvePointSvg(pts[edgeIndex]);
    const p2 = resolvePointSvg(pts[(edgeIndex + 1) % pts.length]);
    setDraggingFreeDrawEdge({
      connId,
      edgeIndex,
      startSvg: { x: sx, y: sy },
      p1Start: { x: p1.x, y: p1.y },
      p2Start: { x: p2.x, y: p2.y },
    });
    lockScroll();
  };

  /**
   * Przesuń całą krawędź freeDraw równolegle.
   */
  const handleFreeDrawEdgeDragMove = (e) => {
    if (!draggingFreeDrawEdge) return;
    const { x: svgX, y: svgY } = getSvgCoordinates(e);
    const { connId, edgeIndex, startSvg, p1Start, p2Start } = draggingFreeDrawEdge;
    const dx = svgX - startSvg.x;
    const dy = svgY - startSvg.y;
    const pixelsPerMeter = scale / 10;

    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      if (!mc.points || mc.points.length < 3) return mc;
      const pts = mc.points;
      const i1 = edgeIndex;
      const i2 = (edgeIndex + 1) % pts.length;
      const newX1 = p1Start.x + dx;
      const newY1 = p1Start.y + dy;
      const newX2 = p2Start.x + dx;
      const newY2 = p2Start.y + dy;
      const newPoints = [...pts];
      newPoints[i1] = {
        distanceM: (newX1 - 100) / horizontalScale,
        depthM: (newY1 - 80) / pixelsPerMeter,
      };
      newPoints[i2] = {
        distanceM: (newX2 - 100) / horizontalScale,
        depthM: (newY2 - 80) / pixelsPerMeter,
      };
      return { ...mc, points: newPoints };
    }));
  };

  /**
   * Zakończ przeciąganie krawędzi.
   */
  const handleFreeDrawEdgeDragEnd = () => {
    if (!draggingFreeDrawEdge) return;
    setDraggingFreeDrawEdge(null);
    unlockScroll();
  };

  /**
   * Rozpocznij przeciąganie polygonu(ów) freeDraw — wszystkie wierzchołki o ten sam wektor.
   * Jeśli klikany polygon jest częścią `selectedFreeDrawIds`, ciągniemy całą grupę naraz.
   * Inaczej — pojedynczy polygon (klasyczne zachowanie).
   * Przy pierwszym dragu zapamiętuje originalPoints per polygon, żeby można było przywrócić pozycję.
   */
  const handleFreeDrawPolygonDragStart = (e, connId) => {
    e.stopPropagation();
    e.preventDefault();
    const { x: sx, y: sy } = getSvgCoordinates(e);

    // Wyznacz grupę polygonów do dragu. Jeśli klikany jest w zaznaczonej grupie ≥2 — cała grupa.
    let groupIds = [connId];
    if (selectedFreeDrawIds.length >= 2 && selectedFreeDrawIds.includes(connId)) {
      groupIds = [...selectedFreeDrawIds];
    }

    const group = [];
    for (const id of groupIds) {
      const mc = manualConnections.find(m => m.id === id);
      if (!mc || !mc.points || mc.points.length < 3) continue;
      const ptsStart = mc.points.map(p => {
        const svg = resolvePointSvg(p);
        return { x: svg.x, y: svg.y };
      });
      group.push({ connId: id, ptsStart });
    }
    if (group.length === 0) return;

    setDraggingFreeDrawPolygon({
      connId, // główny — dla back-compat
      startSvg: { x: sx, y: sy },
      ptsStart: group[0].ptsStart, // back-compat
      group, // [{ connId, ptsStart }]
    });
    lockScroll();
  };

  /**
   * Przesuń polygon(y) freeDraw o delta. Każdy snap-owany punkt zostaje
   * zamieniony na wolny ({distanceM, depthM}), bo polygon odrywa się od otworów.
   */
  const handleFreeDrawPolygonDragMove = (e) => {
    if (!draggingFreeDrawPolygon) return;
    const { x: svgX, y: svgY } = getSvgCoordinates(e);
    const { startSvg, group } = draggingFreeDrawPolygon;
    const dx = svgX - startSvg.x;
    const dy = svgY - startSvg.y;
    const pixelsPerMeter = scale / 10;

    const byId = new Map(group.map(g => [g.connId, g.ptsStart]));

    setManualConnections(prev => prev.map(mc => {
      const ptsStart = byId.get(mc.id);
      if (!ptsStart) return mc;
      if (!mc.points || mc.points.length !== ptsStart.length) return mc;
      const newPoints = ptsStart.map((p) => ({
        distanceM: (p.x + dx - 100) / horizontalScale,
        depthM: (p.y + dy - 80) / pixelsPerMeter,
      }));
      const next = { ...mc, points: newPoints };
      if (!mc.originalPoints) {
        next.originalPoints = mc.points.map(p => ({ ...p }));
      }
      return next;
    }));
  };

  /**
   * Snap pojedynczego wierzchołka do otworu (konserwatywny próg X — 25px).
   * Zwraca {boreholeId, boundaryIndex} lub {boreholeId, depthFromTop}, albo null gdy daleko od otworów.
   */
  const snapVertexToBoreholeForDrop = (svgX, svgY) => {
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepthUtil(boreholes);
    const SNAP_X_THRESHOLD = 25;

    let closest = null;
    let closestDist = Infinity;
    boreholes.forEach((bh) => {
      const bhX = 100 + bh.distance * horizontalScale;
      const dist = Math.abs(svgX - bhX);
      if (dist < closestDist && dist < SNAP_X_THRESHOLD) {
        closestDist = dist;
        closest = bh;
      }
    });
    if (!closest) return null;

    const depthOffset = (maxDepth - closest.depth) * pixelsPerMeter;
    const topY = 80 + depthOffset;
    const boundaries = [{ y: topY, idx: 0 }];
    let cumT = 0;
    closest.layers.forEach((layer, i) => {
      cumT += layer.thickness;
      boundaries.push({ y: topY + cumT * pixelsPerMeter, idx: i + 1 });
    });

    const EDGE_Y_THRESHOLD = 8;
    let closestEdge = null;
    let minD = Infinity;
    boundaries.forEach(b => {
      const d = Math.abs(svgY - b.y);
      if (d < minD) { minD = d; closestEdge = b; }
    });

    if (closestEdge && minD <= EDGE_Y_THRESHOLD) {
      return { boreholeId: closest.id, boundaryIndex: closestEdge.idx };
    }
    const maxLayerY = boundaries[boundaries.length - 1].y;
    const clampedY = Math.max(topY, Math.min(maxLayerY, svgY));
    return { boreholeId: closest.id, depthFromTop: (clampedY - topY) / pixelsPerMeter };
  };

  /**
   * Zakończ przeciąganie polygonu — przy upuszczeniu każdy wierzchołek niezależnie
   * łapie się do otworu jeśli jest blisko (X < 25px), inaczej zostaje wolny.
   * Polygon może zmienić kształt, jeśli różne wierzchołki złapią różne otwory.
   */
  const handleFreeDrawPolygonDragEnd = () => {
    if (!draggingFreeDrawPolygon) return;
    const { group } = draggingFreeDrawPolygon;
    const groupIds = new Set(group.map(g => g.connId));

    setManualConnections(prev => prev.map(mc => {
      if (!groupIds.has(mc.id)) return mc;
      if (!mc.points) return mc;
      const newPoints = mc.points.map(p => {
        const svg = resolvePointSvg(p);
        const snapped = snapVertexToBoreholeForDrop(svg.x, svg.y);
        return snapped || p;
      });
      return { ...mc, points: newPoints };
    }));

    setDraggingFreeDrawPolygon(null);
    unlockScroll();
  };

  /**
   * Przesuń zadane poligony freeDraw o (dx_m, dy_m) w metrach.
   * Punkty snap-owane do otworów konwertuje na wolne ({distanceM, depthM}).
   * Zachowuje `originalPoints` (raz, przy pierwszym przesunięciu) — dla "Przywróć pozycję".
   * @param {number[]} ids — lista id poligonów
   * @param {number} dx_m — przesunięcie poziome w metrach (+ = w prawo)
   * @param {number} dy_m — przesunięcie pionowe w metrach (+ = w dół)
   */
  const shiftFreeDrawsByMeters = (ids, dx_m, dy_m) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    if (!dx_m && !dy_m) return;
    const idSet = new Set(ids);
    const pixelsPerMeter = scale / 10;

    setManualConnections(prev => prev.map(mc => {
      if (!idSet.has(mc.id)) return mc;
      if (mc.fromLayerId !== 'freeDraw') return mc;
      if (!mc.points || mc.points.length < 3) return mc;
      const newPoints = mc.points.map(p => {
        const svg = resolvePointSvg(p);
        return {
          distanceM: (svg.x - 100) / horizontalScale + dx_m,
          depthM: (svg.y - 80) / pixelsPerMeter + dy_m,
        };
      });
      const next = { ...mc, points: newPoints };
      if (!mc.originalPoints) {
        next.originalPoints = mc.points.map(p => ({ ...p }));
      }
      return next;
    }));
  };

  /**
   * Przywróć polygon na oryginalną pozycję (kopiuje originalPoints → points).
   */
  const restoreFreeDrawPolygonPosition = (connId) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      if (!mc.originalPoints) return mc;
      const { originalPoints, ...rest } = mc;
      return { ...rest, points: originalPoints.map(p => ({ ...p })) };
    }));
  };

  /**
   * Przywróć wszystkie przesunięte polygony freeDraw.
   */
  const restoreAllFreeDrawPolygonPositions = () => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.fromLayerId !== 'freeDraw' || !mc.originalPoints) return mc;
      const { originalPoints, ...rest } = mc;
      return { ...rest, points: originalPoints.map(p => ({ ...p })) };
    }));
  };

  /**
   * Dodaj wierzchołek na krawędzi polygonu freeDraw.
   * edgeIndex = indeks krawędzi (od punktu i do punktu i+1).
   * svgX, svgY = współrzędne kliknięcia na krawędzi.
   */
  const addFreeDrawVertex = (connId, edgeIndex, svgX, svgY) => {
    const sp = snapPoint(svgX, svgY, connId);

    let newPoint;
    if (sp.boreholeId) {
      newPoint = { boreholeId: sp.boreholeId, boundaryIndex: sp.boundaryIndex };
    } else {
      newPoint = { distanceM: sp.distanceM, depthM: sp.depthM };
    }

    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      const newPoints = [...mc.points];
      newPoints.splice(edgeIndex + 1, 0, newPoint);
      return { ...mc, points: newPoints };
    }));
  };

  /**
   * Usuwa pojedynczy wierzchołek z polygonu freeDraw.
   * Wymaga min. 3 wierzchołków po usunięciu (bo polygon < 3 to nie polygon).
   * Jeśli polygon ma controlPoints (krzywe Bézier), reindeksuje je po usunięciu wierzchołka.
   */
  const removeFreeDrawVertex = (connId, pointIndex) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId) return mc;
      if (!mc.points || mc.points.length <= 3) return mc; // min 3 wierzchołki
      const newPoints = mc.points.filter((_, i) => i !== pointIndex);
      // Reindeksuj controlPoints — krawędź `i` znika, krawędzie >i przesuwają się o -1
      let newControlPoints = mc.controlPoints;
      if (mc.controlPoints) {
        newControlPoints = {};
        const oldN = mc.points.length;
        const removedEdge = pointIndex; // krawędź wychodząca z usuniętego wierzchołka
        const prevEdge = (pointIndex - 1 + oldN) % oldN; // krawędź wchodząca
        for (const key of Object.keys(mc.controlPoints)) {
          const m = key.match(/^edge(\d+)$/);
          if (!m) continue;
          const idx = Number(m[1]);
          if (idx === removedEdge || idx === prevEdge) continue; // porzuć dwie sąsiadujące krawędzie
          const newIdx = idx > removedEdge ? idx - 1 : idx;
          newControlPoints[`edge${newIdx}`] = mc.controlPoints[key];
        }
      }
      return { ...mc, points: newPoints, ...(newControlPoints !== mc.controlPoints && { controlPoints: newControlPoints }) };
    }));
  };

  /**
   * Odcumowuje polygony freeDraw od otworów — zamienia każdy punkt z `boreholeId`/`boundaryIndex`
   * na absolutne `distanceM`/`depthM` na podstawie aktualnej pozycji otworu.
   * Po tej operacji polygony pozostają na swoim miejscu nawet gdy otwór zostanie przesunięty.
   *
   * @param {number|null} connId - id konkretnego polygonu, albo null żeby odpiąć WSZYSTKIE freeDraw
   */
  const detachFreeDrawFromBoreholes = (connId = null) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.fromLayerId !== 'freeDraw' || !mc.points) return mc;
      if (connId !== null && mc.id !== connId) return mc;

      const newPoints = mc.points.map(pt => {
        if (!pt.boreholeId) return pt; // już absolutny
        const meters = pointToMeters(pt);
        return { distanceM: meters.distanceM, depthM: meters.depthM };
      });
      return { ...mc, points: newPoints };
    }));
  };

  /**
   * Przeciwieństwo `detachFreeDrawFromBoreholes`: dla każdego absolutnego punktu sprawdza
   * czy leży blisko (≤5 px SVG) wierzchołka warstwy w jakimś otworze. Jeśli tak — zastępuje
   * absolutną referencją przez `{ boreholeId, boundaryIndex }`. Punkty, które nie trafiają
   * w żaden wierzchołek, zostają absolutne.
   */
  const reattachFreeDrawToBoreholes = (connId = null) => {
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepthUtil(boreholes);
    const TOLERANCE_PX = 5;

    const trySnap = (distanceM, depthM) => {
      const svgX = 100 + (distanceM || 0) * horizontalScale;
      const svgY = 80 + (depthM || 0) * pixelsPerMeter;
      let bestBh = null;
      let bestDx = Infinity;
      boreholes.forEach((bh) => {
        const bhX = 100 + bh.distance * horizontalScale;
        const dx = Math.abs(svgX - bhX);
        if (dx < bestDx) { bestDx = dx; bestBh = bh; }
      });
      if (!bestBh || bestDx > TOLERANCE_PX) return null;
      const depthOffset = (maxDepth - bestBh.depth) * pixelsPerMeter;
      const topY = 80 + depthOffset;
      const boundaries = [{ y: topY, idx: 0 }];
      let cum = 0;
      bestBh.layers.forEach((layer, i) => {
        cum += layer.thickness;
        boundaries.push({ y: topY + cum * pixelsPerMeter, idx: i + 1 });
      });
      let closest = null, minDy = Infinity;
      boundaries.forEach(b => { const d = Math.abs(svgY - b.y); if (d < minDy) { minDy = d; closest = b; } });
      if (closest && minDy <= TOLERANCE_PX) {
        return { boreholeId: bestBh.id, boundaryIndex: closest.idx };
      }
      return null;
    };

    setManualConnections(prev => prev.map(mc => {
      if (mc.fromLayerId !== 'freeDraw' || !mc.points) return mc;
      if (connId !== null && mc.id !== connId) return mc;

      const newPoints = mc.points.map(pt => {
        if (pt.boreholeId) return pt; // już przypięty
        const snap = trySnap(pt.distanceM, pt.depthM);
        return snap || pt;
      });
      return { ...mc, points: newPoints };
    }));
  };

  /**
   * Upraszcza kontur polygonu freeDraw przez redukcję wierzchołków (algorytm Douglas-Peucker).
   * Wierzchołki bliżej linii łączącej sąsiadów niż `toleranceM` (w metrach) zostają usunięte.
   * Dla polygonów zamkniętych — zaczyna od dwóch najbardziej oddalonych wierzchołków jako kotwic.
   * Wymaga min. 3 wierzchołków po uproszczeniu.
   */
  const simplifyFreeDrawPolygon = (connId, toleranceM = 0.5) => {
    setManualConnections(prev => prev.map(mc => {
      if (mc.id !== connId || mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length <= 3) return mc;

      // Punkty w metrach
      const ptsM = mc.points.map(p => {
        const m = pointToMeters(p);
        return { distanceM: m.distanceM, depthM: m.depthM, orig: p };
      });
      const n = ptsM.length;

      // Odległość punktu p od odcinka a-b (w metrach)
      const perpDist = (p, a, b) => {
        const dx = b.distanceM - a.distanceM;
        const dy = b.depthM - a.depthM;
        const len2 = dx * dx + dy * dy;
        if (len2 < 1e-9) {
          const ddx = p.distanceM - a.distanceM;
          const ddy = p.depthM - a.depthM;
          return Math.sqrt(ddx * ddx + ddy * ddy);
        }
        const t = ((p.distanceM - a.distanceM) * dx + (p.depthM - a.depthM) * dy) / len2;
        const cx = a.distanceM + t * dx;
        const cy = a.depthM + t * dy;
        const ddx = p.distanceM - cx;
        const ddy = p.depthM - cy;
        return Math.sqrt(ddx * ddx + ddy * ddy);
      };

      // Klasyczny Douglas-Peucker dla otwartej polilinii
      const dp = (pts) => {
        if (pts.length <= 2) return pts;
        let maxDist = 0, maxIdx = 0;
        for (let i = 1; i < pts.length - 1; i++) {
          const d = perpDist(pts[i], pts[0], pts[pts.length - 1]);
          if (d > maxDist) { maxDist = d; maxIdx = i; }
        }
        if (maxDist > toleranceM) {
          const left = dp(pts.slice(0, maxIdx + 1));
          const right = dp(pts.slice(maxIdx));
          return [...left.slice(0, -1), ...right];
        }
        return [pts[0], pts[pts.length - 1]];
      };

      // Dla polygonu zamkniętego — znajdź dwa najbardziej oddalone wierzchołki jako kotwice,
      // potem zastosuj DP na obu połówkach.
      let anchorA = 0, anchorB = 1, maxDist = 0;
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const dx = ptsM[i].distanceM - ptsM[j].distanceM;
          const dy = ptsM[i].depthM - ptsM[j].depthM;
          const d = dx * dx + dy * dy;
          if (d > maxDist) { maxDist = d; anchorA = i; anchorB = j; }
        }
      }
      // Połówka A→B i połówka B→A (przez koniec/początek tablicy)
      const halfAB = [];
      for (let k = anchorA; k !== anchorB; k = (k + 1) % n) halfAB.push(ptsM[k]);
      halfAB.push(ptsM[anchorB]);
      const halfBA = [];
      for (let k = anchorB; k !== anchorA; k = (k + 1) % n) halfBA.push(ptsM[k]);
      halfBA.push(ptsM[anchorA]);

      const simplifiedAB = dp(halfAB);
      const simplifiedBA = dp(halfBA);
      // Połącz z pominięciem powtarzających się kotwic
      const merged = [...simplifiedAB.slice(0, -1), ...simplifiedBA.slice(0, -1)];
      if (merged.length < 3) return mc; // za mało punktów po uproszczeniu — pomiń

      const newPoints = merged.map(p => p.orig);
      // Po uproszczeniu krzywe Bézier nie mają jednoznacznego mapowania — porzucamy
      const { controlPoints, ...rest } = mc;
      return { ...rest, points: newPoints };
    }));
  };

  /**
   * Upraszcza WSZYSTKIE polygony freeDraw w jednej operacji (Douglas-Peucker).
   * Wierzchołki dzielone między ≥2 polygonami są traktowane jako kotwice
   * i NIE są usuwane — dzięki temu wspólne krawędzi nie rozjeżdżają się.
   * Zwraca liczbę zredukowanych wierzchołków łącznie.
   */
  const simplifyAllFreeDrawPolygons = (toleranceM = 0.5) => {
    const snapTolM = 0.05; // tolerancja matchowania wspólnych wierzchołków
    let removedTotal = 0;

    setManualConnections(prev => {
      // Klucz wierzchołka dla wykrycia "wspólnego" punktu między polygonami
      const keyOf = (p) => {
        if (p.boreholeId != null) return `bh:${p.boreholeId}:${p.boundaryIndex ?? -1}`;
        const m = pointToMeters(p);
        return `xy:${Math.round(m.distanceM / snapTolM)}:${Math.round(m.depthM / snapTolM)}`;
      };

      // Dla każdego klucza — zestaw polygonów które go zawierają
      const ownersByKey = new Map();
      const keysByConnId = new Map();
      prev.forEach(mc => {
        if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length < 3) return;
        const keys = mc.points.map(keyOf);
        keysByConnId.set(mc.id, keys);
        new Set(keys).forEach(k => {
          if (!ownersByKey.has(k)) ownersByKey.set(k, new Set());
          ownersByKey.get(k).add(mc.id);
        });
      });
      const ownerSetStr = (key) => {
        const s = ownersByKey.get(key);
        return s ? [...s].sort().join(',') : '';
      };

      const perpDist = (p, a, b) => {
        const dx = b.distanceM - a.distanceM;
        const dy = b.depthM - a.depthM;
        const len2 = dx * dx + dy * dy;
        if (len2 < 1e-9) {
          const ddx = p.distanceM - a.distanceM;
          const ddy = p.depthM - a.depthM;
          return Math.sqrt(ddx * ddx + ddy * ddy);
        }
        const t = ((p.distanceM - a.distanceM) * dx + (p.depthM - a.depthM) * dy) / len2;
        const cx = a.distanceM + t * dx;
        const cy = a.depthM + t * dy;
        const ddx = p.distanceM - cx;
        const ddy = p.depthM - cy;
        return Math.sqrt(ddx * ddx + ddy * ddy);
      };

      const dp = (pts) => {
        if (pts.length <= 2) return pts;
        let maxDist = 0, maxIdx = 0;
        for (let i = 1; i < pts.length - 1; i++) {
          const d = perpDist(pts[i], pts[0], pts[pts.length - 1]);
          if (d > maxDist) { maxDist = d; maxIdx = i; }
        }
        if (maxDist > toleranceM) {
          const left = dp(pts.slice(0, maxIdx + 1));
          const right = dp(pts.slice(maxIdx));
          return [...left.slice(0, -1), ...right];
        }
        return [pts[0], pts[pts.length - 1]];
      };

      return prev.map(mc => {
        if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length <= 3) return mc;

        const ptsM = mc.points.map(p => {
          const m = pointToMeters(p);
          return { distanceM: m.distanceM, depthM: m.depthM, orig: p };
        });
        const n = ptsM.length;
        const keys = keysByConnId.get(mc.id) || [];
        // Kotwica = junkcja: wierzchołek gdzie zestaw "właścicieli" się zmienia
        // (granica wspólnej krawędzi, T-junction). Wnętrze wspólnej krawędzi NIE jest kotwicą
        // — DP może uprościć, a że jest deterministyczny, oba polygony zredukują te same punkty.
        const owners = keys.map(ownerSetStr);
        const anchorIdxs = [];
        for (let i = 0; i < n; i++) {
          const prev_ = owners[(i - 1 + n) % n];
          const curr = owners[i];
          const next = owners[(i + 1) % n];
          if (curr !== prev_ || curr !== next) anchorIdxs.push(i);
        }

        let merged;
        if (anchorIdxs.length >= 2) {
          // Podziel polygon na segmenty między kolejnymi kotwicami (cyklicznie)
          merged = [];
          for (let i = 0; i < anchorIdxs.length; i++) {
            const startIdx = anchorIdxs[i];
            const endIdx = anchorIdxs[(i + 1) % anchorIdxs.length];
            const segment = [];
            let k = startIdx;
            while (k !== endIdx) {
              segment.push(ptsM[k]);
              k = (k + 1) % n;
            }
            segment.push(ptsM[endIdx]);
            const simplified = dp(segment);
            merged.push(...simplified.slice(0, -1));
          }
        } else {
          // Brak kotwic dzielonych — użyj 2 najdalszych wierzchołków jako kotwic (fallback)
          let anchorA = 0, anchorB = 1, maxDist = 0;
          for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
              const dx = ptsM[i].distanceM - ptsM[j].distanceM;
              const dy = ptsM[i].depthM - ptsM[j].depthM;
              const d = dx * dx + dy * dy;
              if (d > maxDist) { maxDist = d; anchorA = i; anchorB = j; }
            }
          }
          const halfAB = [];
          for (let k = anchorA; k !== anchorB; k = (k + 1) % n) halfAB.push(ptsM[k]);
          halfAB.push(ptsM[anchorB]);
          const halfBA = [];
          for (let k = anchorB; k !== anchorA; k = (k + 1) % n) halfBA.push(ptsM[k]);
          halfBA.push(ptsM[anchorA]);
          const simplifiedAB = dp(halfAB);
          const simplifiedBA = dp(halfBA);
          merged = [...simplifiedAB.slice(0, -1), ...simplifiedBA.slice(0, -1)];
        }

        if (merged.length < 3) return mc;
        removedTotal += n - merged.length;
        const newPoints = merged.map(p => p.orig);
        const { controlPoints, ...rest } = mc;
        return { ...rest, points: newPoints };
      });
    });
    return removedTotal;
  };

  /**
   * Tryb rozszerzania granic — kliknięcie przesuwa najbliższe wierzchołki w kierunku kliknięcia.
   * Siła przesunięcia maleje z odległością (gaussian falloff).
   * connId = id edytowanego polygonu, svgX/svgY = współrzędne kliknięcia.
   */
  const expandFreeDrawBoundary = (connId, svgX, svgY) => {
    const pixelsPerMeter = scale / 10;
    const mc = manualConnections.find(m => m.id === connId);
    if (!mc || !mc.points || mc.points.length < 3) return;

    // Resolve all points to SVG coords
    const pts = mc.points.map(p => resolvePointSvg(p));

    // Promień wpływu w pikselach — punkty w tym promieniu zostaną przesunięte
    const influenceRadius = 120;
    // Siła przesunięcia (0-1) — jak mocno punkt się przesunie w kierunku kliknięcia
    const strength = 0.4;

    // Oblicz odległości od kliknięcia do każdego wierzchołka
    const distances = pts.map(p => Math.sqrt((svgX - p.x) ** 2 + (svgY - p.y) ** 2));

    // Znajdź punkty w promieniu wpływu
    const affectedIndices = distances
      .map((d, i) => ({ dist: d, index: i }))
      .filter(item => item.dist < influenceRadius && item.dist > 0);

    if (affectedIndices.length === 0) return;

    // Zapamiętaj kształt przed zmianą (do wykrywania luk)
    if (freeDrawFillGaps) {
      preDragShapeRef.current = pointsToRing(mc.points, mc.controlPoints);
    } else {
      preDragShapeRef.current = null;
    }

    setManualConnections(prev => prev.map(m => {
      if (m.id !== connId) return m;
      const newPoints = [...m.points];

      affectedIndices.forEach(({ dist, index }) => {
        const pt = pts[index];
        // Gaussian falloff — bliższe punkty przesuwają się mocniej
        const factor = strength * Math.exp(-((dist / influenceRadius) ** 2) * 3);

        // Wektor od punktu do kliknięcia
        const dx = svgX - pt.x;
        const dy = svgY - pt.y;

        // Nowa pozycja SVG
        const newX = pt.x + dx * factor;
        const newY = pt.y + dy * factor;

        // Konwertuj na metry (tylko wolne punkty, snap-owane pomijamy)
        const origPt = m.points[index];
        if (!origPt.boreholeId) {
          newPoints[index] = {
            distanceM: (newX - 100) / horizontalScale,
            depthM: (newY - 80) / pixelsPerMeter,
          };
        }
      });

      return { ...m, points: newPoints };
    }));

    // Apply clipping after expand (only if nakładanie is ON)
    if (freeDrawExpandClip) {
      setTimeout(() => applyFreeDrawEditClipping(connId), 50);
    }
  };

  /**
   * Po zakończeniu edycji wierzchołków — przytnij sąsiednie polygony
   * (edytowany polygon "nadpisuje" nakładające się fragmenty innych).
   */
  const applyFreeDrawEditClipping = (editedId) => {
    setManualConnections(prev => {
      const editedConn = prev.find(mc => mc.id === editedId);
      if (!editedConn || editedConn.fromLayerId !== 'freeDraw' || !editedConn.points || editedConn.points.length < 3) return prev;

      const editedRing = pointsToRing(editedConn.points, editedConn.controlPoints);
      const editedPoly = [[editedRing]];

      const calcArea = (ring) => Math.abs(ring.reduce((sum, pt, i, arr) => {
        const next = arr[(i + 1) % arr.length];
        return sum + (pt[0] * next[1] - next[0] * pt[1]);
      }, 0)) / 2;

      const getLargestRing = (multiPoly) => {
        return multiPoly.reduce((best, poly) =>
          calcArea(poly[0]) > calcArea(best[0]) ? poly : best
        , multiPoly[0]);
      };

      // Oblicz lukę (gap) = stary_kształt - nowy_kształt
      let gapPoly = null;
      if (freeDrawFillGaps && preDragShapeRef.current) {
        try {
          gapPoly = polygonClipping.difference([[preDragShapeRef.current]], editedPoly);
        } catch { /* brak luki */ }
      }

      const otherFreeDraws = prev.filter(mc =>
        mc.id !== editedId && mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3
      );

      // Krok 1: Przytnij sąsiadów (edytowany nachodzi)
      const editedArea = calcArea(editedRing);
      let result = prev.map(mc => {
        if (mc.id === editedId) return mc;
        if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length < 3) return mc;

        const mcRing = pointsToRing(mc.points, mc.controlPoints);
        try {
          const clippedResult = polygonClipping.difference([[mcRing]], editedPoly);
          if (!clippedResult || clippedResult.length === 0) {
            // Pusta różnica = mc całkowicie zawiera się w edytowanym lub jest mu równy.
            // Jeśli mc jest WYRAŹNIE mniejszy → to soczewka (createLensInLayer) celowo
            // narysowana wewnątrz rodzica. Zachowaj ją, jest renderowana na wierzchu (createdAt).
            // Tylko duplikat (taka sama powierzchnia) traktujemy jako prawdziwą nakładkę i usuwamy.
            const mcArea = calcArea(mcRing);
            if (mcArea < editedArea * 0.99) return mc;
            return { ...mc, _removed: true };
          }
          const finalRing = getLargestRing(clippedResult)[0];
          const clippedPoints = ringToPoints(finalRing, mc.points);
          if (clippedPoints.length < 3) return { ...mc, _removed: true };
          return { ...mc, points: clippedPoints, controlPoints: {} };
        } catch { return mc; }
      }).filter(mc => !mc._removed);

      // Krok 2: Wypełnij lukę — rozszerz WSZYSTKICH sąsiadów którzy stykają się z luką.
      // Iterujemy posortowanych po odległości od luki — bliżsi konsumują pierwsi,
      // pozostały kawałek luki dostępny dla kolejnych.
      if (gapPoly && gapPoly.length > 0) {
        const gapRing = gapPoly[0][0];
        const gapCx = gapRing.reduce((s, p) => s + p[0], 0) / gapRing.length;
        const gapCy = gapRing.reduce((s, p) => s + p[1], 0) / gapRing.length;

        // Posortuj sąsiadów wg najmniejszej odległości od centroidu luki
        const sortedNeighbors = result
          .filter(mc => mc.id !== editedId && mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3)
          .map(mc => {
            const mcRing = pointsToRing(mc.points, mc.controlPoints);
            let minDist = Infinity;
            for (const [px, py] of mcRing) {
              const d = (px - gapCx) ** 2 + (py - gapCy) ** 2;
              if (d < minDist) minDist = d;
            }
            return { mc, minDist };
          })
          .sort((a, b) => a.minDist - b.minDist)
          .map(o => o.mc);

        let remainingGap = gapPoly;
        const updates = new Map();

        for (const mc of sortedNeighbors) {
          if (!remainingGap || remainingGap.length === 0) break;
          try {
            const mcRing = pointsToRing(mc.points, mc.controlPoints);
            const mcArea = calcArea(mcRing);
            // Soczewki w warstwie-rodzicu (mc całkowicie wewnątrz edytowanego) nie biorą
            // udziału w gap-fill — luka jest na obwodzie edytowanego, a soczewka leży
            // w środku, więc rozszerzenie skoczyłoby z lokalizacji rodzica na obwód
            // i podmieniłoby soczewkę gap-em.
            const mcDiffEdited = polygonClipping.difference([[mcRing]], editedPoly);
            if (!mcDiffEdited || mcDiffEdited.length === 0) continue;
            const expanded = polygonClipping.union([[mcRing]], remainingGap);
            if (!expanded || expanded.length === 0) continue;

            // Czy sąsiad coś zassał z luki? Sprawdź czy największy kawałek wzrósł wyraźnie.
            const expandedLargest = getLargestRing(expanded);
            const expandedArea = calcArea(expandedLargest[0]);
            if (expandedArea <= mcArea * 1.0001) continue; // nie wzrósł — nie styka się z luką

            // Przytnij do żeby nie nachodził na edytowanego ani innych już rozszerzonych
            const trimmed = polygonClipping.difference(expanded, editedPoly);
            if (!trimmed || trimmed.length === 0) continue;
            const finalRing = getLargestRing(trimmed)[0];
            const expandedPoints = ringToPoints(finalRing, mc.points);
            if (expandedPoints.length < 3) continue;

            updates.set(mc.id, expandedPoints);

            // Pomniejsz pozostałą lukę o pochłonięty fragment
            const consumed = polygonClipping.difference([[finalRing]], [[mcRing]]);
            if (consumed && consumed.length > 0) {
              remainingGap = polygonClipping.difference(remainingGap, consumed);
            }
          } catch { /* pomiń tego sąsiada */ }
        }

        if (updates.size > 0) {
          result = result.map(mc =>
            updates.has(mc.id) ? { ...mc, points: updates.get(mc.id), controlPoints: {} } : mc
          );
        }
      }

      return result;
    });
    // Nie resetujemy preDragShapeRef tutaj — zostanie nadpisany w kolejnym dragStart
  };

  const handleLayerClickForConnection = (boreholeId, layerId) => {
    if (!manualConnectionMode) return;

    if (!selectedLayerForConnection) {
      // Pierwszy wybór - zapisz warstwę źródłową
      setSelectedLayerForConnection({ boreholeId, layerId });
    } else {
      // Drugi wybór - utwórz połączenie
      const fromBorehole = boreholes.find(b => b.id === selectedLayerForConnection.boreholeId);
      const toBorehole = boreholes.find(b => b.id === boreholeId);

      // Sprawdź czy to sąsiednie otwory
      const fromIndex = boreholes.indexOf(fromBorehole);
      const toIndex = boreholes.indexOf(toBorehole);

      if (Math.abs(fromIndex - toIndex) !== 1) {
        alert('Możesz łączyć tylko warstwy z sąsiednich otworów!');
        setSelectedLayerForConnection(null);
        return;
      }

      // Generuj unikalne ID dla nowego połączenia
      const newConnectionId = Math.max(0, ...manualConnections.map(mc => mc.id || 0)) + 1;

      // Znajdź pierwszą warstwę (selectedLayer) aby pobrać jej kolor
      const selectedBorehole = boreholes.find(b => b.id === selectedLayerForConnection.boreholeId);
      const selectedLayer = selectedBorehole?.layers.find(l => l.id === selectedLayerForConnection.layerId);
      const connectionColor = selectedLayer?.color || '#CCCCCC';

      // Zawsze dodaj nowe połączenie (pozwalaj na wiele połączeń między tymi samymi warstwami)
      if (fromIndex < toIndex) {
        setManualConnections([...manualConnections, {
          id: newConnectionId,
          fromBoreholeId: selectedLayerForConnection.boreholeId,
          toBoreholeId: boreholeId,
          fromLayerId: selectedLayerForConnection.layerId,
          toLayerId: layerId,
          color: connectionColor, // Zapisz kolor pierwszej warstwy
          createdAt: Date.now() // Znacznik czasu utworzenia
        }]);
      } else {
        setManualConnections([...manualConnections, {
          id: newConnectionId,
          fromBoreholeId: boreholeId,
          toBoreholeId: selectedLayerForConnection.boreholeId,
          fromLayerId: layerId,
          toLayerId: selectedLayerForConnection.layerId,
          color: connectionColor, // Zapisz kolor pierwszej warstwy
          createdAt: Date.now() // Znacznik czasu utworzenia
        }]);
      }

      // Automatycznie przełącz na tryb interpolacji, żeby pokazać niebieskie punkty
      setManualConnectionMode(false);
      setInterpolationMode(true);
      setConnectLayers(true); // Upewnij się że połączenia są widoczne

      setSelectedLayerForConnection(null);
    }
  };
  /**
   * Wyciąga przedrostek rzymski z nazwy warstwy.
   * Np. "II1" → "II", "VI2" → "VI", "IVa" → "IV", "I" → "I"
   * Obsługuje cyfry rzymskie: I, II, III, IV, V, VI, VII, VIII, IX, X, XI, XII itd.
   */
  const getRomanPrefix = (layerName) => {
    if (!layerName || layerName.trim() === '') return '';
    const match = layerName.trim().match(/^(X{0,3}(?:IX|IV|V?I{0,3}))/i);
    if (match && match[1] && match[1].length > 0) {
      return match[1].toUpperCase();
    }
    return '';
  };

  /**
   * Klasyfikacja profilu zwietrzelinowego na podstawie nazwy gruntu.
   * - SM, ST → skała
   * - Brekacja → skała
   * - KR, KRg → zwietrzlina
   * - KW, KWg → zwietrzlina
   * - Wszystko inne → grunt
   */
  const getWeatheringProfile = (layerName) => {
    if (!layerName || layerName.trim() === '') return '';
    const upper = layerName.trim().toUpperCase();
    if (upper.startsWith('SM') || upper.startsWith('ST')) return 'skala';
    if (upper.startsWith('BREKACJA')) return 'skala';
    if (upper.startsWith('KR')) return 'zwietrzelina';
    if (upper.startsWith('KW')) return 'zwietrzelina';
    return 'grunt';
  };

  const weatheringProfileColors = {
    'grunt': '#8B6914',       // brązowy
    'zwietrzelina': '#DAA520', // żółty
    'skala': '#808080',        // szary
  };

  /**
   * Sprawdza czy dwie warstwy powinny być automatycznie połączone
   * w zależności od wybranego trybu autoConnectMode.
   */
  const shouldAutoConnect = (layer, nextLayer) => {
    if (!autoConnectByName) return false;

    switch (autoConnectMode) {
      case 'byName':
        return layer.name === nextLayer.name && layer.name.trim() !== '';
      case 'byGeotechnicalLayer':
        return layer.geotechnicalLayer && nextLayer.geotechnicalLayer
          && layer.geotechnicalLayer === nextLayer.geotechnicalLayer
          && layer.geotechnicalLayer.toString().trim() !== '';
      case 'byWeatheringProfile': {
        const profileA = getWeatheringProfile(layer.name);
        const profileB = getWeatheringProfile(nextLayer.name);
        return profileA !== '' && profileB !== '' && profileA === profileB;
      }
      case 'byRomanPrefix': {
        const romanA = getRomanPrefix((layer.geotechnicalLayer || '').toString());
        const romanB = getRomanPrefix((nextLayer.geotechnicalLayer || '').toString());
        return romanA !== '' && romanB !== '' && romanA === romanB;
      }
      case 'bySoilGroup': {
        const groupA = getSoilGroup(layer.name);
        const groupB = getSoilGroup(nextLayer.name);
        return groupA !== '' && groupB !== '' && groupA === groupB;
      }
      default:
        return layer.name === nextLayer.name && layer.name.trim() !== '';
    }
  };

  /**
   * Zwraca klucz grupowania warstwy wg aktualnego trybu.
   * Dla byName: nazwa gruntu, byGeotechnicalLayer: nr warstwy, byWeatheringProfile: grunt/zwietrzelina/skala,
   * bySoilGroup: grupa litologiczna (piaski/gliny/pyly/ily/zwiry/pospolki/organiczne/nasyp + skaly/zwietrzeliny)
   */
  const getLayerGroupKey = (layer) => {
    switch (autoConnectMode) {
      case 'byGeotechnicalLayer':
        return (layer.geotechnicalLayer || '').toString().trim();
      case 'byWeatheringProfile':
        return getWeatheringProfile(layer.name);
      case 'byRomanPrefix':
        return getRomanPrefix((layer.geotechnicalLayer || '').toString());
      case 'bySoilGroup':
        return getSoilGroup(layer.name);
      case 'byName':
      default:
        return (layer.name || '').trim();
    }
  };

  /**
   * Scala sąsiadujące warstwy o tym samym kluczu grupowania w grupy.
   * Np. 3 warstwy "grunt" pod sobą → 1 grupa z top pierwszej i bottom ostatniej.
   * Dodatkowo: pojedyncza warstwa otoczona tą samą grupą jest wchłaniana
   * (np. jedna zwietrzelina wśród skał → traktowana jako skała).
   */
  const mergeAdjacentLayers = (layers, depthOffset) => {
    // Krok 1: Podstawowe scalanie sąsiadujących warstw o tym samym kluczu
    const rawGroups = [];
    let currentY = 0;

    layers.forEach((layer) => {
      const layerTop = currentY;
      const layerBottom = currentY + layer.thickness;
      currentY += layer.thickness;

      const key = getLayerGroupKey(layer);
      const lastGroup = rawGroups[rawGroups.length - 1];

      if (lastGroup && lastGroup.key === key && key !== '') {
        lastGroup.bottom = layerBottom + depthOffset;
        lastGroup.layers.push(layer);
      } else {
        rawGroups.push({
          key,
          top: layerTop + depthOffset,
          bottom: layerBottom + depthOffset,
          // bySoilGroup: kolor warstwy wygrywa, ale TYLKO gdy jest świadomy —
          // z numeru warstwy geotechnicznej albo ustawiony ręcznie. Gdy pochodzi
          // z algorytmicznej palety litologii lub z domyślnego szarego (czyli
          // numery warstw nie są jeszcze nadane), bierzemy kolor grupy.
          color: (autoConnectMode === 'byWeatheringProfile' && weatheringProfileColors[key])
            || (autoConnectMode === 'bySoilGroup' && !layerHasOwnColor(layer) && SOIL_GROUP_COLORS[key])
            || layer.color,
          pattern: layer.pattern || 'none',
          name: layer.name,
          layers: [layer],
        });
      }
    });

    // Krok 2 (tylko dla byWeatheringProfile):
    if (autoConnectMode !== 'byWeatheringProfile' || rawGroups.length < 3) {
      return rawGroups;
    }

    // Krok 2a: od pierwszej zwietrzliny w dół — grunt → zwietrzlina
    const firstZwIdx = rawGroups.findIndex(g => g.key === 'zwietrzelina');
    if (firstZwIdx >= 0) {
      for (let i = firstZwIdx + 1; i < rawGroups.length; i++) {
        if (rawGroups[i].key === 'grunt') {
          rawGroups[i].key = 'zwietrzelina';
          rawGroups[i].color = weatheringProfileColors['zwietrzelina'] || rawGroups[i].color;
        }
      }
      // Scal sąsiadujące zwietrzliny po przeklasyfikowaniu
      const remerged = [rawGroups[0]];
      for (let i = 1; i < rawGroups.length; i++) {
        const prev = remerged[remerged.length - 1];
        if (rawGroups[i].key === prev.key && prev.key !== '') {
          prev.bottom = rawGroups[i].bottom;
          prev.layers.push(...rawGroups[i].layers);
        } else {
          remerged.push(rawGroups[i]);
        }
      }
      rawGroups.length = 0;
      rawGroups.push(...remerged);
    }

    // Krok 2b: od pierwszej skały w dół — wszystko to skała
    const firstSkalaIdx = rawGroups.findIndex(g => g.key === 'skala');
    if (firstSkalaIdx >= 0 && firstSkalaIdx < rawGroups.length - 1) {
      const skalaGroup = rawGroups[firstSkalaIdx];
      for (let i = firstSkalaIdx + 1; i < rawGroups.length; i++) {
        skalaGroup.bottom = rawGroups[i].bottom;
        skalaGroup.layers.push(...rawGroups[i].layers);
      }
      rawGroups.length = firstSkalaIdx + 1;
    }

    // Krok 2b: Wchłoń pojedyncze grupy otoczone tym samym kluczem
    if (rawGroups.length < 3) return rawGroups;

    const merged = [rawGroups[0]];
    for (let i = 1; i < rawGroups.length - 1; i++) {
      const prev = merged[merged.length - 1];
      const curr = rawGroups[i];
      const next = rawGroups[i + 1];

      if (curr.layers.length === 1 && prev.key === next.key && prev.key !== '' && curr.key !== prev.key) {
        prev.bottom = curr.bottom;
        prev.layers.push(...curr.layers);
      } else {
        merged.push(curr);
      }
    }

    // Dodaj ostatnią grupę - sprawdź czy można ją scalić z poprzednią
    const lastRaw = rawGroups[rawGroups.length - 1];
    const lastMerged = merged[merged.length - 1];
    if (lastRaw.key === lastMerged.key && lastRaw.key !== '') {
      lastMerged.bottom = lastRaw.bottom;
      lastMerged.layers.push(...lastRaw.layers);
    } else {
      merged.push(lastRaw);
    }

    return merged;
  };

  const findLayerConnections = () => {
    const connections = [];
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepthUtil(boreholes);
    const useMergedGroups = autoConnectByName && (autoConnectMode === 'byGeotechnicalLayer' || autoConnectMode === 'byWeatheringProfile' || autoConnectMode === 'byRomanPrefix' || autoConnectMode === 'bySoilGroup');

    for (let i = 0; i < boreholes.length - 1; i++) {
      const currentBorehole = boreholes[i];
      const nextBorehole = boreholes[i + 1];

      const currentOffset = (maxDepth - currentBorehole.depth) * pixelsPerMeter;
      const nextOffset = (maxDepth - nextBorehole.depth) * pixelsPerMeter;
      const pairStartIdx = connections.length;

      // --- Ręczne połączenia (zawsze warstwa-po-warstwie) ---
      let currentY = 0;
      currentBorehole.layers.forEach((layer) => {
        const layerTop = currentY;
        const layerBottom = currentY + layer.thickness;
        currentY += layer.thickness;

        let nextY = 0;
        nextBorehole.layers.forEach((nextLayer) => {
          const nextLayerTop = nextY;
          const nextLayerBottom = nextY + nextLayer.thickness;
          nextY += nextLayer.thickness;

          // Znajdź WSZYSTKIE ręczne połączenia między tymi warstwami (bez freeDraw)
          const manualConnectionsForLayers = manualConnections.filter(mc =>
            mc.fromLayerId !== 'freeDraw' &&
            mc.fromBoreholeId === currentBorehole.id &&
            mc.toBoreholeId === nextBorehole.id &&
            mc.fromLayerId === layer.id &&
            mc.toLayerId === nextLayer.id
          );

          // Automatyczne połączenie wg nazwy (tryb byName) - warstwa-po-warstwie
          if (!useMergedGroups) {
            const autoConnection = shouldAutoConnect(layer, nextLayer);
            const excludeKey = `${currentBorehole.id}-${nextBorehole.id}-${layer.name}`;
            const isExcluded = excludedAutoConnections.includes(excludeKey);

            if (autoConnection && !isExcluded) {
              connections.push({
                fromBorehole: currentBorehole,
                toBorehole: nextBorehole,
                fromTop: layerTop + currentOffset / pixelsPerMeter,
                fromBottom: layerBottom + currentOffset / pixelsPerMeter,
                toTop: nextLayerTop + nextOffset / pixelsPerMeter,
                toBottom: nextLayerBottom + nextOffset / pixelsPerMeter,
                color: layer.color,
                pattern: layer.pattern || 'none',
                name: layer.name,
                isManual: false,
                connectionId: 'auto'
              });
            }
          }

          // Dodaj WSZYSTKIE ręczne połączenia
          manualConnectionsForLayers.forEach(mc => {
            connections.push({
              fromBorehole: currentBorehole,
              toBorehole: nextBorehole,
              fromTop: layerTop + currentOffset / pixelsPerMeter,
              fromBottom: layerBottom + currentOffset / pixelsPerMeter,
              toTop: nextLayerTop + nextOffset / pixelsPerMeter,
              toBottom: nextLayerBottom + nextOffset / pixelsPerMeter,
              color: mc.color || layer.color,
              pattern: mc.pattern || layer.pattern || 'none',
              name: mc.name || layer.name,
              isManual: true,
              connectionId: mc.id,
              createdAt: mc.createdAt || 0
            });
          });
        });
      });

      // --- Automatyczne połączenia ze scalaniem grup (byGeotechnicalLayer / byWeatheringProfile / byRomanPrefix / bySoilGroup) ---
      if (useMergedGroups) {
        const currentGroups = mergeAdjacentLayers(currentBorehole.layers, currentOffset / pixelsPerMeter);
        const nextGroups = mergeAdjacentLayers(nextBorehole.layers, nextOffset / pixelsPerMeter);

        // Zbierz klucze stref obu otworów (do wykrywania wyklinu)
        const currentKeys = new Set(currentGroups.map(g => g.key).filter(k => k !== ''));
        const nextKeys = new Set(nextGroups.map(g => g.key).filter(k => k !== ''));

        // Indeks wystąpienia grupy w otworze (0, 1, 2...). Dla profilu zwietrzenia
        // zawsze 0 — reguła "od pierwszej zwietrzliny w dół" prostuje profil, więc
        // każdy klucz jest tam unikalny. Dla grup gruntowych już nie: piasek potrafi
        // leżeć nad gliną i pod gliną.
        const occurrenceIndexes = (groups) => {
          const seen = {};
          return groups.map(g => {
            if (g.key === '') return 0;
            const idx = seen[g.key] === undefined ? 0 : seen[g.key] + 1;
            seen[g.key] = idx;
            return idx;
          });
        };
        const currentOcc = occurrenceIndexes(currentGroups);
        const nextOcc = occurrenceIndexes(nextGroups);
        const countKey = (groups, key) => groups.filter(g => g.key === key).length;

        // === UNIWERSALNY WYKLIN ===
        // Kolejność stref z góry na dół. Dla profilu zwietrzenia jest z natury rzeczy
        // stała; dla grup gruntowych nie ma porządku wpisanego w nomenklaturę, więc
        // wyliczamy go z danych — wg średniej głębokości stropu w obu otworach.
        const isWeathering = autoConnectMode === 'byWeatheringProfile';
        let allZoneKeys;
        if (isWeathering) {
          allZoneKeys = ['grunt', 'zwietrzelina', 'skala'];
        } else {
          const avgTop = {};
          [...currentGroups, ...nextGroups].forEach(g => {
            if (g.key === '') return;
            (avgTop[g.key] = avgTop[g.key] || []).push(g.top);
          });
          allZoneKeys = Object.keys(avgTop).sort((a, b) => {
            const mean = (arr) => arr.reduce((x, y) => x + y, 0) / arr.length;
            return mean(avgTop[a]) - mean(avgTop[b]);
          });
        }
        const wedges = {}; // { zoneKey: { currentHas, tipY, boundaryY, sourceGroup } }

        if (isWeathering || autoConnectMode === 'bySoilGroup') {
          const missingKeys = allZoneKeys.filter(k => currentKeys.has(k) !== nextKeys.has(k));

          // Wykryj crossSwap: strefy brakujące po RÓŻNYCH stronach
          // Np. lewy: grunt+skała, prawy: zwietrzelina+skała → grunt idzie do góry zwietrzeliny, zwietrzelina do dołu gruntu
          const currentOnlyKeys = allZoneKeys.filter(k => currentKeys.has(k) && !nextKeys.has(k));
          const nextOnlyKeys = allZoneKeys.filter(k => nextKeys.has(k) && !currentKeys.has(k));
          const isCrossSwap = currentOnlyKeys.length >= 1 && nextOnlyKeys.length >= 1;

          allZoneKeys.forEach(zoneKey => {
            const cHas = currentKeys.has(zoneKey);
            const nHas = nextKeys.has(zoneKey);
            if (cHas === nHas) return;

            const sideWith = cHas ? currentGroups : nextGroups;
            const sideWithout = cHas ? nextGroups : currentGroups;

            // Grupa występująca po stronie "z" więcej niż raz (piasek-glina-piasek)
            // jest niejednoznaczna — nie wiadomo, które wystąpienie ma się wyklinować.
            // Świadomie pomijamy: lepiej brak wyklinu niż wyklin z przypadkowej strefy.
            // Nie dotyczy profilu zwietrzenia — tam klucze są z definicji unikalne.
            if (!isWeathering && countKey(sideWith, zoneKey) > 1) return;

            const sourceGroup = sideWith.find(g => g.key === zoneKey);
            if (!sourceGroup) return;

            // boundaryY = granica ostatniej istniejącej strefy powyżej brakującej (strona bez)
            let boundaryY;
            if (isWeathering) {
              // === ŚCIEŻKA PROFILU ZWIETRZENIA — NIE RUSZAĆ ===
              // Kod dosłownie taki jak przed wprowadzeniem grup gruntowych. Działa
              // i jest sprawdzony na produkcji; generyk niżej go NIE dotyczy.
              if (zoneKey === 'zwietrzelina') {
                const gruntG = sideWithout.find(g => g.key === 'grunt');
                const skalaG = sideWithout.find(g => g.key === 'skala');
                boundaryY = gruntG ? gruntG.bottom : (skalaG ? skalaG.top : null);
              } else if (zoneKey === 'skala') {
                const zwG = sideWithout.find(g => g.key === 'zwietrzelina');
                const gruntG = sideWithout.find(g => g.key === 'grunt');
                boundaryY = zwG ? zwG.bottom : (gruntG ? gruntG.bottom : null);
              } else { // grunt
                const zwG = sideWithout.find(g => g.key === 'zwietrzelina');
                const skalaG = sideWithout.find(g => g.key === 'skala');
                boundaryY = zwG ? zwG.top : (skalaG ? skalaG.top : null);
              }
            } else {
              // === ŚCIEŻKA GRUP GRUNTOWYCH ===
              // Ta sama zasada wyrażona ogólnie: spąg najbliższej istniejącej strefy
              // POWYŻEJ brakującej, a gdy takiej nie ma — strop najbliższej PONIŻEJ.
              // Działa dla dowolnej liczby stref w dowolnej kolejności.
              const zoneIdx = allZoneKeys.indexOf(zoneKey);
              boundaryY = null;
              for (let z = zoneIdx - 1; z >= 0; z--) {
                const g = sideWithout.find(gr => gr.key === allZoneKeys[z]);
                if (g) { boundaryY = g.bottom; break; }
              }
              if (boundaryY === null) {
                for (let z = zoneIdx + 1; z < allZoneKeys.length; z++) {
                  const g = sideWithout.find(gr => gr.key === allZoneKeys[z]);
                  if (g) { boundaryY = g.top; break; }
                }
              }
            }

            if (boundaryY === null) return;

            // Najwyższa strefa brakująca ZAWSZE idzie na pełną szerokość (nie wyklin
            // do połowy) — dla profilu zwietrzenia to grunt. CrossSwap: też pełna szerokość.
            // Gałąź profilu zwietrzenia zostawiona dosłownie, żeby istniejące przekroje
            // renderowały się bit w bit tak samo.
            const isCrossSwapZone = isWeathering
              ? ((zoneKey === 'grunt') || (isCrossSwap && zoneKey === 'zwietrzelina'))
              : ((zoneKey === allZoneKeys[0]) || isCrossSwap);

            let tipY;
            if (isCrossSwapZone) {
              // CrossSwap: boundaryY jest punktem zbiegu na stronie bez strefy
              tipY = boundaryY;
            } else if (missingKeys.length === 1) {
              const midSource = (sourceGroup.top + sourceGroup.bottom) / 2;
              tipY = (boundaryY + midSource) / 2;
            } else {
              tipY = boundaryY;
            }

            wedges[zoneKey] = {
              currentHas: cHas, tipY, boundaryY, sourceGroup,
              isTrapez: !isCrossSwapZone && missingKeys.length > 1,
              isCrossSwap: isCrossSwapZone,
            };
          });
        }

        // Normalne połączenia (oba mają) — z opcjonalnym midPoint przy wyklinie
        currentGroups.forEach((cGroup, cIdx) => {
          if (cGroup.key === '') return;
          nextGroups.forEach((nGroup, nIdx) => {
            if (nGroup.key === '' || cGroup.key !== nGroup.key) return;

            // Ta sama grupa może wystąpić w otworze kilka razy (piasek-glina-piasek).
            // Bez tego warunku każde wystąpienie po lewej łączyłoby się z każdym po
            // prawej — 2×2 zamiast 2 połączeń, w tym dwa krzyżujące się przez cały
            // przekrój. Łączymy n-te wystąpienie z n-tym.
            if (autoConnectMode === 'bySoilGroup' && currentOcc[cIdx] !== nextOcc[nIdx]) return;

            const excludeKey = `${currentBorehole.id}-${nextBorehole.id}-${cGroup.key}`;
            const isExcluded = excludedAutoConnections.includes(excludeKey);
            if (isExcluded) return;

            const conn = {
              fromBorehole: currentBorehole,
              toBorehole: nextBorehole,
              fromTop: cGroup.top,
              fromBottom: cGroup.bottom,
              toTop: nGroup.top,
              toBottom: nGroup.bottom,
              color: cGroup.color,
              pattern: cGroup.pattern,
              name: cGroup.key,
              isManual: false,
              connectionId: 'auto'
            };

            // Łamanie krawędzi przy sąsiednim wyklinie
            if (cGroup.key === 'grunt') {
              const wBelow = wedges['zwietrzelina'] || wedges['skala'];
              if (wBelow) {
                conn.midPoint = {
                  topY: (cGroup.top + nGroup.top) / 2,
                  bottomY: wBelow.tipY,
                };
              }
            } else if (cGroup.key === 'zwietrzelina') {
              const wAbove = wedges['grunt'];
              const wBelow = wedges['skala'];
              // Pomiń midPoint od gruntu gdy grunt jest pełno-szerokościowy (sam pokrywa górę)
              const effectiveAbove = (wAbove && !wAbove.isCrossSwap) ? wAbove : null;
              const effectiveBelow = (wBelow && !wBelow.isCrossSwap) ? wBelow : null;
              if (effectiveAbove || effectiveBelow) {
                conn.midPoint = {
                  topY: effectiveAbove ? effectiveAbove.tipY : (cGroup.top + nGroup.top) / 2,
                  bottomY: effectiveBelow ? effectiveBelow.tipY : (cGroup.bottom + nGroup.bottom) / 2,
                };
              }
            } else if (cGroup.key === 'skala') {
              const wAbove = wedges['zwietrzelina'] || wedges['grunt'];
              if (wAbove && !wAbove.isCrossSwap) {
                conn.midPoint = {
                  topY: wAbove.tipY,
                  bottomY: (cGroup.bottom + nGroup.bottom) / 2,
                };
              }
            }

            connections.push(conn);
          });
        });

        // Wykliny — trójkąt, trapez, lub crossSwap (pełna szerokość)
        Object.entries(wedges).forEach(([zoneKey, { currentHas, tipY, boundaryY, sourceGroup, isTrapez, isCrossSwap: isCross }]) => {
          const excludeKey = `${currentBorehole.id}-${nextBorehole.id}-${zoneKey}`;
          if (excludedAutoConnections.includes(excludeKey)) return;

          if (isCross) {
            // CROSS-SWAP: pełna szerokość, proste linie od sourceGroup do boundaryY (punkt)
            // Grunt idzie do góry zwietrzeliny, zwietrzelina idzie do dołu gruntu
            // Normalny connection bez wedgeEnd i bez midPoint → renderer rysuje trapez Bézier
            connections.push({
              fromBorehole: currentBorehole,
              toBorehole: nextBorehole,
              fromTop: currentHas ? sourceGroup.top : boundaryY,
              fromBottom: currentHas ? sourceGroup.bottom : boundaryY,
              toTop: currentHas ? boundaryY : sourceGroup.top,
              toBottom: currentHas ? boundaryY : sourceGroup.bottom,
              color: sourceGroup.color,
              pattern: sourceGroup.pattern,
              name: zoneKey,
              isManual: false,
              connectionId: 'auto-wedge',
            });
          } else if (isTrapez) {
            connections.push({
              fromBorehole: currentBorehole,
              toBorehole: nextBorehole,
              fromTop: currentHas ? sourceGroup.top : boundaryY,
              fromBottom: currentHas ? sourceGroup.bottom : boundaryY,
              toTop: currentHas ? boundaryY : sourceGroup.top,
              toBottom: currentHas ? boundaryY : sourceGroup.bottom,
              color: sourceGroup.color,
              pattern: sourceGroup.pattern,
              name: zoneKey,
              isManual: false,
              connectionId: 'auto-wedge',
              wedgeEnd: currentHas ? 'right' : 'left',
            });
          } else {
            // TRÓJKĄT: wierzchołek w tipY (interpolowany) w połowie
            connections.push({
              fromBorehole: currentBorehole,
              toBorehole: nextBorehole,
              fromTop: currentHas ? sourceGroup.top : tipY,
              fromBottom: currentHas ? sourceGroup.bottom : tipY,
              toTop: currentHas ? tipY : sourceGroup.top,
              toBottom: currentHas ? tipY : sourceGroup.bottom,
              color: sourceGroup.color,
              pattern: sourceGroup.pattern,
              name: zoneKey,
              isManual: false,
              connectionId: 'auto-wedge',
              wedgeEnd: currentHas ? 'right' : 'left',
            });
          }
        });
      }

      // === Oznacz najwyższą strefę-conn jako rozciągniętą do terenu ===
      // Tylko w trybie byWeatheringProfile (grunt/zwietrzlina/skała wymiennie u góry).
      // Tryb byName zachowuje nazwane warstwy z ich faktyczną grubością.
      if (useMergedGroups && autoConnectMode === 'byWeatheringProfile' && terrainProfile && terrainProfile.length >= 2) {
        const currentTopMeters = currentOffset / pixelsPerMeter;
        const nextTopMeters = nextOffset / pixelsPerMeter;
        const pairConns = connections.slice(pairStartIdx);
        // Najwyższy conn = ten który zaczyna się na top obu otworów (nie wedge, nie midPoint).
        // W trybie crossSwap żaden taki nie istnieje — fix pominięty (rzadki przypadek).
        const topConn = pairConns.find(c =>
          !c.wedgeEnd &&
          !c.midPoint &&
          Math.abs(c.fromTop - currentTopMeters) < 0.001 &&
          Math.abs(c.toTop - nextTopMeters) < 0.001
        );
        if (topConn) topConn.extendsToTerrain = true;
      }

    }

    return connections;
  };

  /**
   * Konwertuje automatyczne/ręczne połączenia warstw na freeDraw polygony.
   * Każde połączenie (trapez) → 4-punktowy polygon z referencjami snap do otworów.
   * Punkty interpolacji Bézier zamieniane na controlPoints krawędzi.
   */
  const convertConnectionsToFreeDraw = () => {
    const connections = findLayerConnections();
    if (connections.length === 0) return;

    const pixelsPerMeter = scale / 10;

    const newFreeDraws = connections.map((conn, idx) => {
      const fromBhId = conn.fromBorehole.id;
      const toBhId = conn.toBorehole.id;
      const midDistanceM = (conn.fromBorehole.distance + conn.toBorehole.distance) / 2;

      // UWAGA: polygony tworzone przez "Połącz warstwy" są ZAKOTWICZONE w absolutnych współrzędnych
      // (distanceM/depthM) — nie referują do otworów. Dzięki temu przesunięcie otworu po konwersji
      // nie rozrywa kształtu polygonu — polygon zostaje na swoim miejscu, a użytkownik może go
      // dalej edytować jak każdy freeDraw.
      const makePoint = (bh, _bhId, depth) => {
        return { distanceM: bh.distance, depthM: depth };
      };

      const points = [];

      if (conn.wedgeEnd) {
        // WYKLIN — trójkąt (fromTop≈fromBottom) lub trapez (fromTop≠fromBottom)
        if (conn.wedgeEnd === 'left') {
          const isTriangle = Math.abs(conn.fromTop - conn.fromBottom) < 0.01;
          points.push({ distanceM: midDistanceM, depthM: conn.fromTop });
          points.push(makePoint(conn.toBorehole, toBhId, conn.toTop));
          points.push(makePoint(conn.toBorehole, toBhId, conn.toBottom));
          if (!isTriangle) {
            points.push({ distanceM: midDistanceM, depthM: conn.fromBottom });
          }
        } else {
          points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromTop));
          points.push({ distanceM: midDistanceM, depthM: conn.toTop });
          if (Math.abs(conn.toTop - conn.toBottom) >= 0.01) {
            points.push({ distanceM: midDistanceM, depthM: conn.toBottom });
          }
          points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromBottom));
        }
      } else if (conn.midPoint) {
        // 6-PUNKTOWY WIELOKĄT — trapez z łamaniem w połowie
        points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromTop));   // lewy-góra
        points.push({ distanceM: midDistanceM, depthM: conn.midPoint.topY }); // mid-góra
        points.push(makePoint(conn.toBorehole, toBhId, conn.toTop));          // prawy-góra
        points.push(makePoint(conn.toBorehole, toBhId, conn.toBottom));       // prawy-dół
        points.push({ distanceM: midDistanceM, depthM: conn.midPoint.bottomY }); // mid-dół
        points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromBottom)); // lewy-dół
      } else {
        // NORMALNY TRAPEZ — 4 punkty. Dla extendsToTerrain dane pozostają zwykłym 4-trapezem;
        // renderer rozciąga górę nad teren i tnie clipPath'em (mc.extendsToTerrain ↓ przekazane niżej).
        points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromTop));    // lewy-góra
        points.push(makePoint(conn.toBorehole, toBhId, conn.toTop));          // prawy-góra
        points.push(makePoint(conn.toBorehole, toBhId, conn.toBottom));       // prawy-dół
        points.push(makePoint(conn.fromBorehole, fromBhId, conn.fromBottom)); // lewy-dół
      }

      // Konwertuj interpolationPoints na controlPoints krawędzi (tylko dla normalnych trapezów)
      const controlPoints = {};
      if (!conn.wedgeEnd && !conn.midPoint) {
      const connectionKeyTop = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'top', conn.connectionId);
      const connectionKeyBottom = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'bottom', conn.connectionId);

      const cpTop = interpolationPoints[connectionKeyTop];
      const cpBottom = interpolationPoints[connectionKeyBottom];

      if (cpTop) {
        // edge0 = krawędź od punktu 0 (lewy-góra) do punktu 1 (prawy-góra)
        // Oblicz środek krawędzi w pikselach SVG
        const x1 = 100 + conn.fromBorehole.distance * horizontalScale;
        const x2 = 100 + conn.toBorehole.distance * horizontalScale;
        const y1Top = 80 + conn.fromTop * pixelsPerMeter;
        const y2Top = 80 + conn.toTop * pixelsPerMeter;
        const midTopX = (x1 + x2) / 2;
        const midTopY = (y1Top + y2Top) / 2;
        controlPoints['edge0'] = {
          offsetDistanceM: (cpTop.x - midTopX) / horizontalScale,
          offsetDepthM: (cpTop.y - midTopY) / pixelsPerMeter,
        };
      }
      if (cpBottom) {
        // edge2 = krawędź od punktu 2 (prawy-dół) do punktu 3 (lewy-dół)
        const x1 = 100 + conn.fromBorehole.distance * horizontalScale;
        const x2 = 100 + conn.toBorehole.distance * horizontalScale;
        const y1Bottom = 80 + conn.fromBottom * pixelsPerMeter;
        const y2Bottom = 80 + conn.toBottom * pixelsPerMeter;
        const midBottomX = (x1 + x2) / 2;
        const midBottomY = (y1Bottom + y2Bottom) / 2;
        controlPoints['edge2'] = {
          offsetDistanceM: (cpBottom.x - midBottomX) / horizontalScale,
          offsetDepthM: (cpBottom.y - midBottomY) / pixelsPerMeter,
        };
      }
      } // zamknięcie if (!conn.wedgeEnd && !conn.midPoint)

      return {
        id: Date.now() + idx,
        fromBoreholeId: fromBhId,
        toBoreholeId: toBhId,
        fromLayerId: 'freeDraw',
        toLayerId: 'freeDraw',
        color: conn.color,
        pattern: conn.pattern || 'none',
        name: conn.name,
        points,
        controlPoints,
        createdAt: Date.now() + idx,
        convertedFromAuto: true, // oznacz że pochodzi z konwersji
        ...(conn.extendsToTerrain ? { extendsToTerrain: true } : {}),
      };
    });

    // Dodaj skonwertowane polygony do manualConnections, usuwając stare ręczne (nie-freeDraw).
    // Przy okazji ODCUMUJ istniejące freeDraw od otworów — po Połącz warstwy wszystkie polygony
    // mają mieć absolutne współrzędne, niezależne od pozycji otworów.
    setManualConnections(prev => {
      const existingFreeDraws = prev
        .filter(mc => mc.fromLayerId === 'freeDraw')
        .map(mc => {
          if (!mc.points) return mc;
          const newPoints = mc.points.map(pt => {
            if (!pt.boreholeId) return pt;
            const meters = pointToMeters(pt);
            return { distanceM: meters.distanceM, depthM: meters.depthM };
          });
          return { ...mc, points: newPoints };
        });
      return [...existingFreeDraws, ...newFreeDraws];
    });

    // Wyłącz automatyczne połączenia (zastąpione przez freeDraw) ale zostaw connectLayers ON
    setAutoConnectByName(false);
    setExcludedAutoConnections([]);
    setConnectLayers(true);
  };

  /**
   * Pomocnicza: oblicza pole powierzchni ringu polygon-clipping.
   */
  const calcRingArea = (ring) => Math.abs(ring.reduce((sum, pt, i, arr) => {
    const next = arr[(i + 1) % arr.length];
    return sum + (pt[0] * next[1] - next[0] * pt[1]);
  }, 0)) / 2;

  /**
   * Pomocnicza: wybiera największy polygon z wyniku polygon-clipping.
   */
  const getLargestPoly = (multiPoly) => {
    if (!multiPoly || multiPoly.length === 0) return null;
    return multiPoly.reduce((best, poly) =>
      calcRingArea(poly[0]) > calcRingArea(best[0]) ? poly : best
    , multiPoly[0]);
  };

  /**
   * Pomocnicza: sortuje freeDraw polygony od góry do dołu (wg średniej głębokości).
   */
  const sortFreeDrawsByDepth = (freeDraws) => {
    return freeDraws.map(fd => {
      const avgDepth = fd.points.reduce((sum, p) => {
        const m = pointToMeters(p);
        return sum + m.depthM;
      }, 0) / fd.points.length;
      return { fd, avgDepth };
    }).sort((a, b) => a.avgDepth - b.avgDepth);
  };

  /**
   * Przycina nakładające się freeDraw polygony.
   * Iteruje od góry (najwyższy = najmniejszy depthM) do dołu.
   * Każdy wyższy polygon przycina te pod nim za pomocą polygon-clipping.difference().
   */
  const clipOverlappingFreeDraws = () => {
    const freeDraws = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3);
    if (freeDraws.length < 2) return;

    const sorted = sortFreeDrawsByDepth(freeDraws);

    const processedRings = [];
    const updatedMap = new Map();

    for (const { fd } of sorted) {
      const currentRing = pointsToRing(fd.points, fd.controlPoints);

      if (processedRings.length > 0) {
        let clippedPoly = [[currentRing]];
        for (const upperRing of processedRings) {
          try {
            clippedPoly = polygonClipping.difference(clippedPoly, [[upperRing]]);
            if (!clippedPoly || clippedPoly.length === 0) { clippedPoly = null; break; }
          } catch { /* kontynuuj */ }
        }

        if (!clippedPoly || clippedPoly.length === 0) {
          updatedMap.set(fd.id, null);
        } else {
          const largest = getLargestPoly(clippedPoly);
          const clippedPoints = ringToPoints(largest[0], fd.points);
          if (clippedPoints.length >= 3) {
            updatedMap.set(fd.id, clippedPoints);
          } else {
            updatedMap.set(fd.id, null);
          }
        }
      }

      processedRings.push(currentRing);
    }

    if (updatedMap.size === 0) return;

    setManualConnections(prev => {
      return prev
        .map(mc => {
          if (!updatedMap.has(mc.id)) return mc;
          const newPoints = updatedMap.get(mc.id);
          if (newPoints === null) return { ...mc, _removed: true };
          return { ...mc, points: newPoints, controlPoints: {} };
        })
        .filter(mc => !mc._removed);
    });
  };

  /**
   * Wypełnia luki między warstwami freeDraw.
   * Algorytm (od góry do dołu, per para otworów):
   * 1. Dla każdego polygonu: rozszerz górne punkty (przy otworach) o 50px w górę
   * 2. Przytnij rozszerzony polygon kumulatywnym unionem wyższych warstw
   * 3. Wynik = polygon bez luki, idealnie przylegający do warstwy wyżej
   */
  const fillGapsBetweenFreeDraws = () => {
    const freeDraws = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3);
    if (freeDraws.length < 2) return;

    const pixelsPerMeter = scale / 10;
    const expandPx = 50; // 50px rozszerzenia w górę
    const expandM = expandPx / pixelsPerMeter; // przelicz na metry

    // Grupuj freeDraw po parze otworów (fromBoreholeId-toBoreholeId)
    const byBhPair = {};
    for (const fd of freeDraws) {
      const key = `${fd.fromBoreholeId}-${fd.toBoreholeId}`;
      if (!byBhPair[key]) byBhPair[key] = [];
      byBhPair[key].push(fd);
    }

    const updatedMap = new Map();

    // Dla każdej pary otworów: przetwarzaj warstwy od góry do dołu
    for (const pairKey of Object.keys(byBhPair)) {
      const pairFds = byBhPair[pairKey];

      // Sortuj od góry (najwyższy = najmniejszy avgDepth)
      const sorted = sortFreeDrawsByDepth(pairFds);

      let cumulativeUpperPoly = null; // kumulatywny union wyższych warstw (w metrach)

      for (let i = 0; i < sorted.length; i++) {
        const { fd } = sorted[i];
        const currentRing = pointsToRing(fd.points, fd.controlPoints);

        if (cumulativeUpperPoly !== null) {
          try {
            // Rozszerz: union oryginalnego ringu z kopią przesuniętą o expandM w górę.
            // Dół zostaje na miejscu, góra się podnosi o 50px.
            const shiftedRing = currentRing.map(pt => [pt[0], pt[1] - expandM]);
            let expandedPoly = polygonClipping.union([[currentRing]], [[shiftedRing]]);
            if (!expandedPoly || expandedPoly.length === 0) continue;

            // Przytnij rozszerzony polygon kumulatywnym unionem wyższych
            const clipped = polygonClipping.difference(expandedPoly, cumulativeUpperPoly);
            if (clipped && clipped.length > 0) {
              const largest = getLargestPoly(clipped);
              if (largest) {
                const newPoints = ringToPoints(largest[0], fd.points);
                if (newPoints.length >= 3) {
                  updatedMap.set(fd.id, newPoints);
                }
              }
            }
          } catch { /* kontynuuj */ }
        }

        // Dodaj ORYGINALNY ring do kumulatywnego union (nie rozszerzony)
        try {
          if (cumulativeUpperPoly === null) {
            cumulativeUpperPoly = [[currentRing]];
          } else {
            const newCum = polygonClipping.union(cumulativeUpperPoly, [[currentRing]]);
            if (newCum && newCum.length > 0) {
              cumulativeUpperPoly = newCum;
            }
          }
        } catch { /* kontynuuj */ }
      }
    }

    if (updatedMap.size === 0) return;

    setManualConnections(prev => {
      return prev.map(mc => {
        if (!updatedMap.has(mc.id)) return mc;
        const newPoints = updatedMap.get(mc.id);
        if (!newPoints) return mc;
        return { ...mc, points: newPoints, controlPoints: {} };
      });
    });
  };

  return {
    // States
    connectLayers, setConnectLayers,
    autoConnectByName, setAutoConnectByName,
    autoConnectMode, setAutoConnectMode,
    manualConnections, setManualConnections,
    selectedLayerForConnection, setSelectedLayerForConnection,
    excludedAutoConnections, setExcludedAutoConnections,
    connectionPointAdjustments, setConnectionPointAdjustments,
    draggingConnectionPoint, setDraggingConnectionPoint,
    manualConnectionMode, setManualConnectionMode,
    interpolationMode, setInterpolationMode,
    interpolationPoints, setInterpolationPoints,
    draggingInterpolation, setDraggingInterpolation,
    hoveredConnection, setHoveredConnection,
    freeDrawMode, setFreeDrawMode,
    freeDrawClipMode, setFreeDrawClipMode,
    freeDrawPoints, setFreeDrawPoints,
    freeDrawPreview, setFreeDrawPreview,
    selectedFreeDraw, setSelectedFreeDraw,
    selectedFreeDrawIds, setSelectedFreeDrawIds,
    editingFreeDraw, setEditingFreeDraw,
    freeDrawEditVerticesMode, setFreeDrawEditVerticesMode,
    freeDrawSnapVertices, setFreeDrawSnapVertices,
    freeDrawFillGaps, setFreeDrawFillGaps,
    freeDrawExpandMode, setFreeDrawExpandMode,
    freeDrawExpandClip, setFreeDrawExpandClip,
    draggingFreeDrawPoint,
    deleteFreeDraw,
    addFreeDrawVertex,
    removeFreeDrawVertex,
    simplifyFreeDrawPolygon,
    simplifyAllFreeDrawPolygons,
    detachFreeDrawFromBoreholes,
    reattachFreeDrawToBoreholes,
    expandFreeDrawBoundary,
    applyFreeDrawEditClipping,
    handleFreeDrawPointDragStart,
    handleFreeDrawPointDragMove,
    handleFreeDrawPointDragEnd,
    draggingFreeDrawControl,
    handleFreeDrawControlDragStart,
    handleFreeDrawControlDragMove,
    handleFreeDrawControlDragEnd,
    resetFreeDrawControlPoint,
    draggingFreeDrawEdge,
    handleFreeDrawEdgeDragStart,
    handleFreeDrawEdgeDragMove,
    handleFreeDrawEdgeDragEnd,
    draggingFreeDrawPolygon,
    handleFreeDrawPolygonDragStart,
    handleFreeDrawPolygonDragMove,
    handleFreeDrawPolygonDragEnd,
    restoreFreeDrawPolygonPosition,
    restoreAllFreeDrawPolygonPositions,
    shiftFreeDrawsByMeters,
    convertConnectionsToFreeDraw,
    clipOverlappingFreeDraws,
    fillGapsBetweenFreeDraws,

    // Utility re-exports
    getConnectionKey,
    getConnectionPointKey,
    resolvePointSvg,

    // Functions
    findLayerConnections,
    handleInterpolationPointDrag,
    handleInterpolationMouseMove,
    handleInterpolationMouseUp,
    resetInterpolationPoint,
    handleConnectionPointMouseDown,
    handleConnectionPointMouseMove,
    handleConnectionPointMouseUp,
    resetConnectionPoint,
    deleteManualConnection,
    deleteConnection,
    handleLayerClickForConnection,
    snapToLayerBoundary,
    snapToFreeDrawVertex,
    handleFreeDrawClick,
    applyOrthoSnap,
    handleFreeDrawDoubleClick,
    splitMode, setSplitMode,
    splittingConnId, setSplittingConnId,
    splitLinePoints, setSplitLinePoints,
    splitPickMode, setSplitPickMode,
    splitLinePreview, setSplitLinePreview,
    contextMenuFreeDraw, setContextMenuFreeDraw,
    splitFreeDrawConnection,
    handleSplitClick,
    cancelSplit,
    polylineSplitMode, setPolylineSplitMode,
    polylineSplitPoints, setPolylineSplitPoints,
    polylineSplitPreview, setPolylineSplitPreview,
    polylineSplitStatus, setPolylineSplitStatus,
    handlePolylineSplitClick,
    finishPolylineSplit,
    cancelPolylineSplit,
    lensInLayerMode, setLensInLayerMode,
    lensInLayerParentId, setLensInLayerParentId,
    createLensInLayer,
    cancelLensInLayer,
    copiedFreeDrawProps, setCopiedFreeDrawProps,
    editPropsDialog, setEditPropsDialog,
    updateFreeDrawProps,
    copyFreeDrawProps,
    pasteFreeDrawProps,
    bringFreeDrawToFront,
    sendFreeDrawToBack,
    cutHoleMode, setCutHoleMode,
    cutHoleParentId, setCutHoleParentId,
    cutHoleInFreeDraw,
    cancelCutHole,
    smoothFreeDrawEdges,
    mergePolygonsMode, setMergePolygonsMode,
    mergeFromConnId, setMergeFromConnId,
    mergeFreeDrawPolygons,
    draggingPolygonLabel,
    handlePolygonLabelDragStart, handlePolygonLabelDragMove, handlePolygonLabelDragEnd,
    resetPolygonLabelPos,
    stickyLayerLock, setStickyLayerLock,
  };
}
